const $$splitComponentImporter = () => import('/Users/xpress/Code/knrt/jgxt/projects/jgxt/ui/web/src/routes/console/_auth/_layout/health-plan/edit.$planId.tsx?tsr-split=component');
import { lazyRouteComponent } from '@tanstack/react-router';
import { ArrowsRotateLeft, ChevronDown, ChevronUp, Eye, Xmark } from '@gravity-ui/icons';
import { Button, Chip, Dropdown, ScrollShadow, Spinner, Switch, Tabs } from '@heroui/react';
import { createFileRoute } from '@tanstack/react-router';
import dayjs from 'dayjs';
import type { ReactNode } from 'react';
import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useCurrentAccountQuery } from '@/api/account';
import { useExerciseGuideOptionsQuery } from '@/api/exercise-guide';
import { type HealthIssueEvidence, type HealthIssueItem, type HealthIssueLedger, type HealthIssueLedgerState, type HealthIssueStatus, type HealthPlanMedicalReferenceGroup, type HealthPlanOriginalReport, useClaimEditMutation, useConsultTransferMutation, useHealthPlanDetailQuery, useHealthPlanIssueLedgerAIQuery, useHealthPlanIssueLedgerRefreshMutation, useHealthPlanMedicalReferencesQuery, useHealthPlanOriginalReportMutation, useHealthPlanPublishMutation, useHealthPlanUpdateMutation, useReviewRemindersAiGenerateMutation } from '@/api/health-plan';
import type { EvidenceModule, EvidenceSnapshot, ExerciseGuideOption, ExerciseRxItem, HealthPlanDetail, MedicationDose, MonitoringItem, PatientEvidenceFact, ProductRecommendation, ReviewReminderItem } from '@/api/model';
import { type PatientDetail, usePatientDetailQuery } from '@/api/patient';
import { type MedicationFieldsInput, type MedicationHisCandidate, type MedicationReviewStatus, type PatientMedicationItem, useMedicationAddFromHisMutation, useMedicationAddMutation, useMedicationArchiveMutation, useMedicationReorderMutation, useMedicationResetHisMutation, useMedicationRestoreMutation, useMedicationSaveMutation, usePatientMedicationListQuery } from '@/api/patient-medication';
import { DateField } from '@/components/common/DateField';
import { PageContainer } from '@/components/common/PageContainer';
import { SimpleInput } from '@/components/common/SimpleInput';
import { SimpleMultiSelect } from '@/components/common/SimpleMultiSelect';
import { SimpleNumberInput } from '@/components/common/SimpleNumberInput';
import { SimpleSelect } from '@/components/common/SimpleSelect';
import { SimpleTextArea } from '@/components/common/SimpleTextArea';
import { HealthPlanConsultDialog, type HealthPlanConsultValues } from '@/components/health-plan/HealthPlanConsultDialog';
import { type HealthPlanModule, HealthPlanModuleIcon, type HealthPlanModuleTone, healthPlanModuleMeta, healthPlanModuleToneClass } from '@/components/health-plan/PlanModuleMeta';
import { PatientSelfMedicationList } from '@/components/patient/PatientSelfMedicationList';
import { notify } from '@/lib/feedback';
import { genderLabel } from '@/lib/gender';
import { claimEditHealthPlanWithConfirm } from '@/lib/health-plan-claim';
import { isMedicationAsNeeded, medicationDoseTimes, medicationMealRelationOptions, medicationReminderDefaults, medicationReminderTimeOptions, medicationReminderTimesCount, monitoringSlotsByType, timeSlotLabelMap } from '@/lib/health-plan-enums';
import { canClaimEdit, canConsultTransfer, canCoordinateConsultTransfer, canEditPlanMedication, canSavePlan, hasRole, isOwnerOf, ROLE_SUPER_ADMIN } from '@/lib/role';
export const Route = createFileRoute('/console/_auth/_layout/health-plan/edit/$planId')({
  component: lazyRouteComponent($$splitComponentImporter, 'component', () => Route.ssr)
});
interface HealthPlanFormState {
  endDate: string | null;
  diet: TextListEditItem[];
  // 每日推荐摄入目标的原始输入,空串表示未设定(跟随公式),保存时转数字校验
  dietCalorieTarget: string;
  nutrition: TextListEditItem[];
  tcmRegulation: TextListEditItem[];
  // 运动和用药是结构化处方，直接编辑数组（不走文本模块的增删改追踪）
  exerciseRx: ExerciseRxItem[];
  monitoring: StructuredEditItem<MonitoringItem>[];
  reviewReminders: ReviewReminderItem[];
  evidenceSelections: {
    module: EvidenceModule;
    atomId: string;
  }[];
}
interface MedicalReferenceTriggerProps {
  medicalReferenceCount?: number;
  attachedReferenceCount?: number;
  onOpenMedicalReference?: () => void;
}
type TextModuleKey = 'diet' | 'nutrition' | 'tcmRegulation';
interface TextListEditItem {
  id: string;
  text: string;
  source: 'current' | 'added';
  deleted: boolean;
}
interface StructuredEditItem<T> {
  id: string;
  value: T;
  source: 'current' | 'added';
  deleted: boolean;
}
interface OriginalReportState {
  evidence: HealthIssueEvidence;
  report: HealthPlanOriginalReport | null;
  error?: string;
}

// 每日推荐摄入目标的合法范围(千卡/天),与服务端 healthplan.DietCalorieTarget* 一致
const DIET_CALORIE_TARGET_MIN = 500;
const DIET_CALORIE_TARGET_MAX = 6000;
const monitoringTypeOptions = [{
  label: '血压',
  value: 'blood_pressure'
}, {
  label: '血糖',
  value: 'blood_glucose'
}, {
  label: '体重',
  value: 'weight'
}, {
  label: '体温',
  value: 'temperature'
}];
const frequencyOptions = [{
  label: '每日',
  value: 'daily'
}, {
  label: '每周',
  value: 'weekly'
}];
const medicationFrequencyOptions = [{
  label: '每日 1 次（QD）',
  value: 'QD'
}, {
  label: '每日 2 次（BID）',
  value: 'BID'
}, {
  label: '每日 3 次（TID）',
  value: 'TID'
}, {
  label: '每日 4 次（QID）',
  value: 'QID'
}, {
  label: '每 6 小时 1 次（Q6H）',
  value: 'Q6H'
}, {
  label: '每 8 小时 1 次（Q8H）',
  value: 'Q8H'
}, {
  label: '每 12 小时 1 次（Q12H）',
  value: 'Q12H'
}, {
  label: '隔日 1 次（QOD）',
  value: 'QOD'
}, {
  label: '每周 1 次（QW）',
  value: 'QW'
}, {
  label: '每周 2 次（BIW）',
  value: 'BIW'
}, {
  label: '每日早晨 1 次（QAM）',
  value: 'QAM'
}, {
  label: '每日晚上 1 次（QPM）',
  value: 'QPM'
}, {
  label: '每日睡前 1 次（QHS）',
  value: 'QHS'
}, {
  label: '饭前服用（AC）',
  value: 'AC'
}, {
  label: '饭后服用（PC）',
  value: 'PC'
}, {
  label: '必要时服用（PRN）',
  value: 'PRN'
}, {
  label: '立即服用 1 次（ST）',
  value: 'ST'
}, {
  label: '仅服用 1 次（ONCE）',
  value: 'ONCE'
}];
const healthPlanWorkspaceModules: {
  key: HealthPlanModule;
  label: string;
}[] = [{
  key: 'diet',
  label: '饮食'
}, {
  key: 'nutrition',
  label: '营养'
}, {
  key: 'exercise',
  label: '运动'
}, {
  key: 'monitoring',
  label: '监测'
}, {
  key: 'medication',
  label: '用药'
}, {
  key: 'review',
  label: '复查'
}, {
  key: 'tcm',
  label: '中医'
}, {
  key: 'recommendation',
  label: '产品'
}];
const reviewReminderCategoryOptions = [{
  label: '门诊复诊',
  value: 'follow_up'
}, {
  label: '检验复查',
  value: 'lab'
}, {
  label: '检查复查',
  value: 'exam'
}, {
  label: '体检复查',
  value: 'physical_exam'
}, {
  label: '用药复评',
  value: 'medication'
}];
const reviewReminderSourceOptions = [{
  label: '门诊记录',
  value: 'outpatient'
}, {
  label: '疾病史',
  value: 'disease'
}, {
  label: '当前用药',
  value: 'medication'
}, {
  label: '体检数据',
  value: 'physical_exam'
}, {
  label: '人工补充',
  value: 'manual'
}];

// 按监测类型展示完整合法时段,并补入已有方案中的历史值,避免编辑时静默隐藏
function getMonitoringTimeSlotOptions(type: string, currentValues: string[]): {
  label: string;
  value: string;
}[] {
  const typeOptions = monitoringSlotsByType[type] ?? [];
  const knownValues = new Set(typeOptions.map(option => option.value));
  return [...typeOptions, ...currentValues.filter(value => !knownValues.has(value)).map(value => ({
    value,
    label: timeSlotLabelMap[value] ?? `历史时段（${value}）`
  }))];
}
const emptyHealthIssueLedger: HealthIssueLedger = {
  summary: '',
  issues: [],
  currentCount: 0,
  pendingCount: 0,
  resolvedCount: 0
};
function buildTextListEditItems(items: string[]): TextListEditItem[] {
  return (items ?? []).map((text, idx) => ({
    id: `current-${idx}`,
    text,
    source: 'current',
    deleted: false
  }));
}
function buildStructuredEditItems<T>(items: T[]): StructuredEditItem<T>[] {
  return (items ?? []).map((value, idx) => ({
    id: `current-${idx}`,
    value,
    source: 'current',
    deleted: false
  }));
}
function buildInitialForm(plan: HealthPlanDetail): HealthPlanFormState {
  return {
    endDate: plan.endDate,
    diet: buildTextListEditItems(plan.diet ?? []),
    dietCalorieTarget: plan.dietCalorieTarget === null ? '' : String(plan.dietCalorieTarget),
    nutrition: buildTextListEditItems(plan.nutrition ?? []),
    tcmRegulation: buildTextListEditItems(plan.tcmRegulation ?? []),
    exerciseRx: (plan.exerciseRx ?? []).map(e => ({
      ...e,
      frequencyCount: e.frequency === 'daily' ? 1 : e.frequencyCount
    })),
    monitoring: buildStructuredEditItems(plan.monitoring ?? []),
    reviewReminders: (plan.reviewReminders ?? []).map(item => ({
      ...item,
      checkItems: [...(item.checkItems ?? [])],
      preparations: [...(item.preparations ?? [])],
      sourceTypes: [...(item.sourceTypes ?? [])],
      reviewStatus: item.reviewStatus || 'pending_review',
      reviewedBy: item.reviewedBy || '',
      reviewedAt: item.reviewedAt || ''
    })),
    evidenceSelections: (plan.evidenceSnapshot ?? []).map(item => ({
      module: item.module,
      atomId: item.atomId
    }))
  };
}
export function HealthPlanWorkspacePage({
  planId,
  initialEditing = false
}: {
  planId: string;
  initialEditing?: boolean;
}) {
  const planQuery = useHealthPlanDetailQuery(planId);
  const {
    data: plan,
    isLoading
  } = planQuery;
  const {
    data: patientDetail
  } = usePatientDetailQuery(plan?.patientId ?? '');
  const issueLedgerQuery = useHealthPlanIssueLedgerAIQuery(planId);
  const medicalReferenceQuery = useHealthPlanMedicalReferencesQuery(planId);
  const issueLedgerState = issueLedgerQuery.data;
  const patient = patientDetail?.patient;
  const {
    data: currentUser
  } = useCurrentAccountQuery();
  // 运动类型只能选指导库里上架的编码，下拉选项与服务端 AI 枚举同源
  const {
    data: exerciseGuideOptions = []
  } = useExerciseGuideOptionsQuery();
  const [editing, setEditing] = useState(initialEditing);
  const [selectedIssueId, setSelectedIssueId] = useState<string | null>(null);
  const [activeModule, setActiveModule] = useState<HealthPlanModule>('monitoring');
  const [originalReportState, setOriginalReportState] = useState<OriginalReportState | null>(null);
  const [form, setForm] = useState<HealthPlanFormState | null>(null);
  const [lastSeededPlanId, setLastSeededPlanId] = useState<string | null>(null);
  if (planId !== lastSeededPlanId) {
    if (form !== null) setForm(null);
    if (plan && plan.id === planId) {
      setForm(buildInitialForm(plan));
      setLastSeededPlanId(planId);
    }
  } else if (plan && form === null) {
    setForm(buildInitialForm(plan));
  }
  const updateMutation = useHealthPlanUpdateMutation();
  const publishMutation = useHealthPlanPublishMutation();
  const claimEditMutation = useClaimEditMutation();
  const consultTransferMutation = useConsultTransferMutation();
  const originalReportMutation = useHealthPlanOriginalReportMutation();
  const issueLedgerRefreshMutation = useHealthPlanIssueLedgerRefreshMutation();
  const reviewRemindersAiGenerateMutation = useReviewRemindersAiGenerateMutation();
  const [consultDialogOpen, setConsultDialogOpen] = useState(false);
  if (isLoading || plan && !form) {
    return <div className="flex justify-center py-20">
        <Spinner />
      </div>;
  }
  if (!plan || !form) {
    return <PageContainer>
        <div className="py-12 text-center text-muted">方案不存在</div>
      </PageContainer>;
  }
  const owner = isOwnerOf(currentUser, plan);
  const canSave = canSavePlan(currentUser, plan);
  const consultBlocked = plan.inConsultation && !owner;
  const editingActive = editing && canSave && !consultBlocked;
  const canStartEdit = canClaimEdit(currentUser, plan, {
    healthManagerId: patient?.healthManagerId ?? null
  });
  const showConsult = canConsultTransfer(currentUser, plan, {
    healthManagerId: patient?.healthManagerId ?? null
  });
  const canCoordinateConsult = !editingActive && canCoordinateConsultTransfer(currentUser, plan);
  const superAdmin = hasRole(currentUser, ROLE_SUPER_ADMIN);
  const exerciseQuestionnaireTitle = patientDetail?.aiPlan.pendingQuestionnaires.find(item => item.questionnaireId === 'exercise-risk')?.title;
  const tcmQuestionnaireTitle = patientDetail?.aiPlan.pendingQuestionnaires.find(item => item.questionnaireId === 'tcm-constitution')?.title;
  const getMedicalReferenceGroup = (module: EvidenceModule) => medicalReferenceQuery.data?.groups.find(group => group.module === module);
  const activeReferenceModule: EvidenceModule | null = activeModule === 'diet' || activeModule === 'nutrition' || activeModule === 'exercise' || activeModule === 'monitoring' || activeModule === 'medication' ? activeModule : null;
  const toggleEvidenceSelection = (module: EvidenceModule, atomId: string) => setForm(current => {
    if (!current) return current;
    const selected = current.evidenceSelections.some(item => item.module === module && item.atomId === atomId);
    return {
      ...current,
      evidenceSelections: selected ? current.evidenceSelections.filter(item => !(item.module === module && item.atomId === atomId)) : [...current.evidenceSelections, {
        module,
        atomId
      }]
    };
  });
  const addTextListItem = (key: TextModuleKey) => setForm(s => s ? {
    ...s,
    [key]: [...s[key], {
      id: `added-${Date.now()}-${s[key].length}`,
      text: '',
      source: 'added',
      deleted: false
    }]
  } : s);
  const updateTextListItem = (key: TextModuleKey, id: string, value: string) => setForm(s => {
    if (!s) return s;
    return {
      ...s,
      [key]: s[key].map(item => item.id === id ? {
        ...item,
        text: value
      } : item)
    };
  });
  const setTextListItemDeleted = (key: TextModuleKey, id: string, deleted: boolean) => setForm(s => {
    if (!s) return s;
    return {
      ...s,
      [key]: s[key].map(item => item.id === id ? {
        ...item,
        deleted
      } : item)
    };
  });
  const removeTextListAddedItem = (key: TextModuleKey, id: string) => setForm(s => {
    if (!s) return s;
    return {
      ...s,
      [key]: s[key].filter(item => item.id !== id)
    };
  });
  const addMonitoring = () => setForm(s => s ? {
    ...s,
    monitoring: [...s.monitoring, {
      id: `added-monitoring-${Date.now()}-${s.monitoring.length}`,
      source: 'added',
      deleted: false,
      value: {
        type: 'blood_pressure',
        frequency: 'daily',
        frequencyCount: 1,
        timeSlots: []
      }
    }]
  } : s);
  const removeMonitoring = (idx: number) => setForm(s => {
    if (!s) return s;
    const item = s.monitoring[idx];
    if (!item) return s;
    if (item.source === 'added') {
      return {
        ...s,
        monitoring: s.monitoring.filter((_, i) => i !== idx)
      };
    }
    return {
      ...s,
      monitoring: s.monitoring.map((it, i) => i === idx ? {
        ...it,
        deleted: true
      } : it)
    };
  });
  const updateMonitoring = (idx: number, patch: Partial<MonitoringItem>) => setForm(s => {
    if (!s) return s;
    const next = [...s.monitoring];
    const item = next[idx];
    if (!item) return s;
    next[idx] = {
      ...item,
      value: {
        ...item.value,
        ...patch
      }
    };
    return {
      ...s,
      monitoring: next
    };
  });
  const addReviewReminder = () => setForm(s => s === null ? s : {
    ...s,
    reviewReminders: [...s.reviewReminders, {
      id: `manual-${Date.now()}-${s.reviewReminders.length}`,
      title: '',
      category: 'follow_up',
      dueDate: dayjs().format('YYYY-MM-DD'),
      department: '',
      reason: '',
      checkItems: [],
      preparations: [],
      sourceTypes: ['manual'],
      reviewStatus: 'pending_review',
      reviewedBy: '',
      reviewedAt: ''
    }]
  });
  const removeReviewReminder = (idx: number) => setForm(s => s === null ? s : {
    ...s,
    reviewReminders: s.reviewReminders.filter((_, i) => i !== idx)
  });
  const updateReviewReminder = (idx: number, patch: Partial<ReviewReminderItem>) => setForm(s => s === null ? s : {
    ...s,
    reviewReminders: s.reviewReminders.map((item, i) => i === idx ? {
      ...item,
      ...patch,
      reviewStatus: 'pending_review',
      reviewedBy: '',
      reviewedAt: ''
    } : item)
  });
  const setReviewReminderStatus = (idx: number, reviewStatus: ReviewReminderItem['reviewStatus']) => setForm(s => s === null ? s : {
    ...s,
    reviewReminders: s.reviewReminders.map((item, i) => i === idx ? {
      ...item,
      reviewStatus,
      reviewedBy: '',
      reviewedAt: ''
    } : item)
  });
  const addExerciseRx = () => setForm(s => s === null ? s : {
    ...s,
    exerciseRx: [...s.exerciseRx, {
      title: '',
      // 空串即「未指定类型」；处方 id 由服务端在保存时补
      taskType: '',
      id: undefined,
      frequency: 'weekly',
      frequencyCount: 3,
      description: ''
    }]
  });
  const updateExerciseRx = (idx: number, patch: Partial<ExerciseRxItem>) => setForm(s => s === null ? s : {
    ...s,
    exerciseRx: s.exerciseRx.map((e, i) => i === idx ? {
      ...e,
      ...patch
    } : e)
  });
  const removeExerciseRx = (idx: number) => setForm(s => s === null ? s : {
    ...s,
    exerciseRx: s.exerciseRx.filter((_, i) => i !== idx)
  });
  const buildUpdatePayload = () => ({
    id: plan.id,
    endDate: form.endDate ?? undefined,
    diet: composeTextList(form.diet),
    dietCalorieTarget: form.dietCalorieTarget.trim() === '' ? null : Number(form.dietCalorieTarget.trim()),
    nutrition: composeTextList(form.nutrition),
    tcmRegulation: composeTextList(form.tcmRegulation),
    exerciseRx: form.exerciseRx.filter(e => e.title.trim() !== ''),
    monitoring: composeStructuredList(form.monitoring),
    reviewReminders: form.reviewReminders,
    evidenceSelections: form.evidenceSelections
  });

  // 摄入目标为空串表示未设定(跟随公式),非空必须是范围内整数
  const validateDietCalorieTarget = () => {
    const raw = form.dietCalorieTarget.trim();
    if (raw === '') return true;
    const value = Number(raw);
    if (!Number.isInteger(value) || value < DIET_CALORIE_TARGET_MIN || value > DIET_CALORIE_TARGET_MAX) {
      notify.warning(`每日推荐摄入目标需为 ${DIET_CALORIE_TARGET_MIN}-${DIET_CALORIE_TARGET_MAX} 之间的整数千卡`);
      return false;
    }
    return true;
  };
  const validateReviewReminders = () => {
    if (form.reviewReminders.some(item => item.title.trim() === '' || !item.dueDate)) {
      notify.warning('请填写所有复查提醒的标题和日期');
      return false;
    }
    return true;
  };
  const handleGenerateReviewReminders = async () => {
    try {
      const items = await reviewRemindersAiGenerateMutation.mutateAsync(plan.id);
      setForm(current => current ? {
        ...current,
        reviewReminders: items
      } : current);
      if (items.length === 0) {
        notify.warning('当前患者数据不足以生成明确的复查提醒');
      } else {
        notify.success(`已生成 ${items.length} 条复查提醒，请审核后发布`);
      }
    } catch (error) {
      notify.error(error instanceof Error ? error.message : '生成复查提醒失败，请重试');
    }
  };
  const handleStartEdit = async () => {
    try {
      const ready = await claimEditHealthPlanWithConfirm(plan.id, claimEditMutation);
      if (!ready) return;
      const refreshed = await planQuery.refetch();
      if (refreshed.data) setForm(buildInitialForm(refreshed.data));
      setEditing(true);
    } catch (e) {
      const msg = e instanceof Error ? e.message : '接管方案失败';
      notify.error(msg);
    }
  };
  const handleCancelEdit = () => {
    setForm(buildInitialForm(plan));
    setConsultDialogOpen(false);
    setEditing(false);
  };
  const handlePublish = async () => {
    if (!validateDietCalorieTarget()) return;
    if (!validateReviewReminders()) return;
    // 保存写入草稿（无草稿自动开），随后发布：冻结草稿为新版本并重编译打卡任务
    await updateMutation.mutateAsync(buildUpdatePayload());
    await publishMutation.mutateAsync({
      planId: plan.id
    });
    notify.success('已保存并发布');
    const refreshed = await planQuery.refetch();
    if (refreshed.data) setForm(buildInitialForm(refreshed.data));
    setEditing(false);
  };
  const openConsultDialog = () => setConsultDialogOpen(true);
  const confirmConsultTransfer = async (values: HealthPlanConsultValues) => {
    const coordinating = canCoordinateConsult && !editingActive;
    if (editingActive && !coordinating) {
      if (!validateDietCalorieTarget()) return;
      if (!validateReviewReminders()) return;
      await updateMutation.mutateAsync(buildUpdatePayload());
    }
    await consultTransferMutation.mutateAsync({
      planId: plan.id,
      ...values
    });
    notify.success(coordinating ? '已转交专科医生会诊' : '已发起会诊');
    setConsultDialogOpen(false);
    const refreshed = await planQuery.refetch();
    if (refreshed.data) setForm(buildInitialForm(refreshed.data));
    setEditing(false);
  };
  const openOriginalReport = async (evidence: HealthIssueEvidence) => {
    if (!evidence.reportId) {
      notify.error('证据未绑定原报告，无法核验');
      return;
    }
    setOriginalReportState({
      evidence,
      report: null
    });
    try {
      const report = await originalReportMutation.mutateAsync({
        planId,
        reportId: evidence.reportId
      });
      setOriginalReportState(current => current?.evidence.id === evidence.id && current.evidence.reportId === evidence.reportId ? {
        evidence,
        report
      } : current);
    } catch (e) {
      const msg = e instanceof Error ? e.message : '原报告加载失败';
      notify.error(msg);
      setOriginalReportState(current => current?.evidence.id === evidence.id && current.evidence.reportId === evidence.reportId ? {
        evidence,
        report: null,
        error: msg
      } : current);
    }
  };
  const refreshIssueLedger = async () => {
    try {
      await issueLedgerRefreshMutation.mutateAsync(planId);
      notify.success('已开始刷新健康关注');
      issueLedgerQuery.refetch();
    } catch (e) {
      const msg = e instanceof Error ? e.message : '刷新健康关注失败';
      notify.error(msg);
    }
  };
  return <PageContainer className="gap-5 bg-surface">
      <div className="flex flex-wrap items-start justify-between gap-4 rounded-[10px] border border-border bg-surface px-5 py-4">
        <div className="max-w-[760px]">
          <h2 className="m-0 text-[18px] font-extrabold leading-7">
            健康方案工作台
          </h2>
          <p className="m-0 mt-1 text-[13px] font-bold leading-5 text-muted">
            三列分别展示健康关注、健康方案和医学参考；
            {editingActive ? '调整方案时可同步选择方案依据。' : '查看时可对照患者事实审阅方案。'}
          </p>
          <div className="mt-4 flex flex-wrap items-center gap-3 text-[12px] font-extrabold">
            <span>{editPatientSummary(patientDetail, plan)}</span>
            <InfoPill tone="blue">{primaryDiseaseText(plan)}</InfoPill>
            <InfoPill tone="green">AI持续管理</InfoPill>
            <InfoPill tone="blue">
              负责人：{plan.ownerName || '未指派'}
            </InfoPill>
            <InfoPill tone={plan.inConsultation ? 'orange' : 'default'}>
              {plan.inConsultation ? '会诊中' : editingActive ? '编辑模式' : '查看模式'}
            </InfoPill>
            <InfoPill tone="orange">
              最近补充：{recentSupplementText(plan)}
            </InfoPill>
          </div>
        </div>
        <div className="flex flex-col items-end gap-2">
          <div className="flex flex-wrap justify-end gap-2">
            {canCoordinateConsult && <Button variant="tertiary" onPress={openConsultDialog}>
                转交专科会诊
              </Button>}
            {showConsult && <Button variant="secondary" style={{
            color: '#000'
          }} isPending={consultTransferMutation.isPending || updateMutation.isPending} onPress={openConsultDialog}>
                发起会诊
              </Button>}
            {!editingActive && canStartEdit && <Button variant="primary" isPending={claimEditMutation.isPending} onPress={handleStartEdit}>
                编辑方案
              </Button>}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 items-start gap-4 xl:grid-cols-[minmax(0,1fr)_minmax(0,1.2fr)_minmax(0,1fr)]">
        <HealthIssueLedgerPanel state={issueLedgerState} ledger={issueLedgerState?.ledger ?? emptyHealthIssueLedger} loading={issueLedgerQuery.isLoading} refreshing={issueLedgerRefreshMutation.isPending} selectedIssueId={selectedIssueId} onSelectIssue={setSelectedIssueId} onRefresh={superAdmin ? undefined : refreshIssueLedger} onOpenOriginalReport={openOriginalReport} />

        <div className="min-w-0 space-y-4 xl:max-h-[calc(100vh-5rem)] xl:min-h-0 xl:overflow-y-auto xl:overscroll-contain xl:pr-1 xl:[contain:layout_paint]">
          <Tabs className="w-full space-y-4" selectedKey={activeModule} variant="secondary" onSelectionChange={key => setActiveModule(key as HealthPlanModule)}>
            <section className="rounded-[10px] border border-border bg-surface p-4">
              <div className="flex flex-wrap items-start justify-between gap-4">
                <div>
                  <h2 className="m-0 text-[16px] font-extrabold leading-6">
                    健康方案
                  </h2>
                  <p className="m-0 mt-1 text-[12px] font-bold leading-5 text-muted">
                    {editingActive ? '保存后覆盖当前方案，AI 按人工调整后的内容继续执行。' : '选择方案模块，右侧医学参考会同步切换。'}
                  </p>
                </div>
                <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2">
                  <div className="flex flex-col gap-1">
                    <span className="text-[12px] font-bold text-muted">
                      开始日期（方案周锚点，不可改）
                    </span>
                    <span className="text-[14px] font-semibold leading-8">
                      {plan.startDate ? dayjs(plan.startDate).format('YYYY-MM-DD') : '-'}
                    </span>
                  </div>
                  <EditFieldLabel label="结束日期">
                    <DateField className="w-full" value={form.endDate} isDisabled={!editingActive} onChange={value => setForm(s => s ? {
                    ...s,
                    endDate: value
                  } : s)} />
                  </EditFieldLabel>
                </div>
              </div>

              <Tabs.ListContainer className="mt-4">
                <Tabs.List aria-label="健康方案分类">
                  {healthPlanWorkspaceModules.map(item => <Tabs.Tab key={item.key} id={item.key} className="whitespace-nowrap px-2">
                      {item.label}
                      <Tabs.Indicator />
                    </Tabs.Tab>)}
                </Tabs.List>
              </Tabs.ListContainer>
            </section>

            <Tabs.Panel id="diet" className="outline-none">
              <TextModuleCard icon={<HealthPlanModuleIcon module="diet" />} title={healthPlanModuleMeta.diet.title} tone={healthPlanModuleMeta.diet.tone} items={form.diet} expanded placeholder="请输入饮食方案" editable={editingActive} onAdd={() => addTextListItem('diet')} onDelete={id => setTextListItemDeleted('diet', id, true)} onRemoveAdded={id => removeTextListAddedItem('diet', id)} onUpdate={(id, value) => updateTextListItem('diet', id, value)} />
              <DietCalorieTargetCard plan={plan} patientDetail={patientDetail} editable={editingActive} value={form.dietCalorieTarget} onChange={value => setForm(s => s === null ? s : {
              ...s,
              dietCalorieTarget: value
            })} />
            </Tabs.Panel>

            <Tabs.Panel id="nutrition" className="outline-none">
              <TextModuleCard icon={<HealthPlanModuleIcon module="nutrition" />} title={healthPlanModuleMeta.nutrition.title} tone={healthPlanModuleMeta.nutrition.tone} items={form.nutrition} expanded placeholder="请输入营养方案" editable={editingActive} onAdd={() => addTextListItem('nutrition')} onDelete={id => setTextListItemDeleted('nutrition', id, true)} onRemoveAdded={id => removeTextListAddedItem('nutrition', id)} onUpdate={(id, value) => updateTextListItem('nutrition', id, value)} />
            </Tabs.Panel>

            <Tabs.Panel id="exercise" className="outline-none">
              <ExerciseRxCard items={form.exerciseRx} guideOptions={exerciseGuideOptions} editable={editingActive} expanded questionnaireTitle={exerciseQuestionnaireTitle} onAdd={addExerciseRx} onUpdate={updateExerciseRx} onRemove={removeExerciseRx} />
            </Tabs.Panel>

            <Tabs.Panel id="monitoring" className="outline-none">
              <MonitoringModuleCard monitoring={form.monitoring} editable={editingActive} expanded onAddMonitoring={addMonitoring} onRemoveMonitoring={removeMonitoring} onUpdateMonitoring={updateMonitoring} />
            </Tabs.Panel>

            <Tabs.Panel id="medication" className="outline-none">
              <MedicationModuleCard planId={plan.id} patientId={plan.patientId} editing={editingActive} canEdit={canEditPlanMedication(currentUser)} expanded />
            </Tabs.Panel>

            <Tabs.Panel id="review" className="outline-none">
              <ReviewReminderModuleCard items={form.reviewReminders} editable={editingActive} expanded onAdd={addReviewReminder} onRemove={removeReviewReminder} onUpdate={updateReviewReminder} onReview={setReviewReminderStatus} onGenerate={handleGenerateReviewReminders} generating={reviewRemindersAiGenerateMutation.isPending} />
            </Tabs.Panel>

            <Tabs.Panel id="tcm" className="outline-none">
              <TextModuleCard icon={<HealthPlanModuleIcon module="tcm" />} title={healthPlanModuleMeta.tcm.title} tone={healthPlanModuleMeta.tcm.tone} questionnaireTitle={tcmQuestionnaireTitle} items={form.tcmRegulation} expanded placeholder="请输入中医调理内容" editable={editingActive} onAdd={() => addTextListItem('tcmRegulation')} onDelete={id => setTextListItemDeleted('tcmRegulation', id, true)} onRemoveAdded={id => removeTextListAddedItem('tcmRegulation', id)} onUpdate={(id, value) => updateTextListItem('tcmRegulation', id, value)} hint="中医调理建议自动同步自最近1年内体检数据（中医体质辨识），超过1年不自动采用" />
            </Tabs.Panel>

            <Tabs.Panel id="recommendation" className="outline-none">
              <RecommendationModuleCard items={plan.recommendations} expanded />
            </Tabs.Panel>
          </Tabs>

          {consultBlocked && <Notice tone="warning">
              该方案正在由专科医生 {plan.consultDoctorName || '—'}
              会诊处理中，发布前其他人无法编辑。
            </Notice>}
          {editing && !editingActive && !consultBlocked && <Notice>
              当前方案负责人是「{plan.ownerName || '未指派'}
              」，您无法保存或发布；如需接管请点击页面上方的「编辑方案」。
            </Notice>}
        </div>

        <MedicalReferencePanel planModule={activeModule} module={activeReferenceModule} group={activeReferenceModule ? getMedicalReferenceGroup(activeReferenceModule) : undefined} snapshots={activeReferenceModule ? (plan.evidenceSnapshot ?? []).filter(item => item.module === activeReferenceModule) : []} selectedAtomIds={activeReferenceModule ? form.evidenceSelections.filter(item => item.module === activeReferenceModule).map(item => item.atomId) : []} facts={medicalReferenceQuery.data?.facts ?? []} knowledgeVersion={medicalReferenceQuery.data?.knowledgeVersion ?? ''} disclaimer={medicalReferenceQuery.data?.disclaimer ?? ''} editable={editingActive && Boolean(activeReferenceModule && getMedicalReferenceGroup(activeReferenceModule)?.canAttach)} loading={medicalReferenceQuery.isLoading} getReferenceCount={module => getMedicalReferenceGroup(module)?.references.length ?? 0} onModuleChange={setActiveModule} onToggle={toggleEvidenceSelection} />
      </div>

      {editingActive && <section className="flex flex-wrap items-center justify-between gap-4 rounded-[10px] border border-warning/40 bg-surface px-5 py-4">
          <div className="text-[17px] font-bold leading-6 text-muted">
            发布后生效逻辑：保存更新 -&gt; AI继续执行 -&gt; 人工修改记录留痕
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="tertiary" onPress={handleCancelEdit}>
              取消编辑
            </Button>
            {showConsult && <Button variant="secondary" style={{
          color: '#000'
        }} isPending={consultTransferMutation.isPending || updateMutation.isPending} isDisabled={consultBlocked} onPress={openConsultDialog}>
                发起会诊
              </Button>}
            <Button variant="primary" isPending={publishMutation.isPending || updateMutation.isPending} isDisabled={!canSave || consultBlocked} onPress={handlePublish}>
              保存并发布
            </Button>
          </div>
        </section>}

      <HealthPlanConsultDialog isOpen={consultDialogOpen} onClose={() => setConsultDialogOpen(false)} onConfirm={confirmConsultTransfer} coordinating={canCoordinateConsult} isPending={consultTransferMutation.isPending || updateMutation.isPending} />
      <OriginalReportDock state={originalReportState} loading={originalReportMutation.isPending} onClose={() => setOriginalReportState(null)} />
    </PageContainer>;
}
function HealthIssueLedgerPanel({
  state,
  ledger,
  loading,
  refreshing,
  selectedIssueId,
  onSelectIssue,
  onRefresh,
  onOpenOriginalReport
}: {
  state?: HealthIssueLedgerState;
  ledger: HealthIssueLedger;
  loading: boolean;
  refreshing: boolean;
  selectedIssueId: string | null;
  onSelectIssue: (id: string) => void;
  onRefresh?: () => void;
  onOpenOriginalReport: (evidence: HealthIssueEvidence) => void;
}) {
  const [activeStatus, setActiveStatus] = useState<HealthIssueStatus>('current');
  const [evidenceOpen, setEvidenceOpen] = useState(false);
  const visibleIssues = ledger.issues.filter(issue => issue.status === activeStatus);
  const selectedIssue = visibleIssues.find(issue => issue.id === selectedIssueId) ?? visibleIssues[0] ?? null;
  const switchStatus = (status: HealthIssueStatus) => {
    setActiveStatus(status);
    setEvidenceOpen(false);
    const firstIssue = ledger.issues.find(issue => issue.status === status);
    if (firstIssue) onSelectIssue(firstIssue.id);
  };
  const selectIssue = (issue: HealthIssueItem) => {
    onSelectIssue(issue.id);
    setEvidenceOpen(true);
  };
  return <aside className="flex flex-col gap-3 xl:sticky xl:top-4 xl:max-h-[calc(100vh-2rem)] xl:min-h-0 xl:overflow-hidden">
      <section className="flex min-h-0 flex-1 flex-col rounded-[10px] border border-border bg-surface p-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="m-0 text-[18px] font-extrabold leading-7">
              健康关注
            </h2>
            <p className="m-0 mt-1 text-[12px] font-bold leading-5 text-muted">
              {issueLedgerStateText(state)}
            </p>
          </div>
          {onRefresh && <Button size="sm" variant="tertiary" isPending={refreshing} onPress={onRefresh}>
              <ArrowsRotateLeft className="size-4" />
              刷新
            </Button>}
        </div>

        {state?.errorMessage && <div className="mt-3 rounded-md bg-danger-soft px-3 py-2 text-[12px] font-bold leading-5 text-danger">
            {state.errorMessage}
          </div>}
        {state?.stale && state.status === 'running' && <div className="mt-3 rounded-md bg-accent-soft px-3 py-2 text-[12px] font-bold leading-5 text-accent">
            健康关注正在后台刷新
          </div>}
        {ledger.summary && <div className="mt-3 rounded-md bg-surface-secondary px-3 py-2 text-[13px] font-medium leading-6 text-foreground">
            {ledger.summary}
          </div>}

        <div className="mt-4 grid grid-cols-3 gap-1 rounded-lg bg-surface-secondary p-1">
          <IssueStatusTab label="当前异常" count={ledger.currentCount} active={activeStatus === 'current'} tone="red" onClick={() => switchStatus('current')} />
          <IssueStatusTab label="待复查" count={ledger.pendingCount} active={activeStatus === 'pending_review'} tone="orange" onClick={() => switchStatus('pending_review')} />
          <IssueStatusTab label="已缓解" count={ledger.resolvedCount} active={activeStatus === 'resolved'} tone="green" onClick={() => switchStatus('resolved')} />
        </div>

        {loading ? <div className="mt-4 rounded-lg border border-dashed border-border bg-surface-secondary px-4 py-8 text-center">
            <Spinner />
          </div> : ledger.issues.length === 0 ? <div className="mt-4">
            <EmptyState text={state?.status === 'running' ? '健康关注生成中' : '暂无可展示的报告问题'} />
          </div> : visibleIssues.length === 0 ? <div className="mt-4">
            <EmptyState text={`暂无${issueStatusLabel(activeStatus)}问题`} />
          </div> : <div className="mt-4 min-h-0 flex-1 space-y-2 overflow-y-auto pr-1">
            {visibleIssues.map(issue => <IssueLedgerCard key={issue.id} issue={issue} selected={selectedIssue?.id === issue.id} onClick={() => selectIssue(issue)} />)}
          </div>}
      </section>

      <IssueEvidenceDock issue={selectedIssue} open={evidenceOpen} onClose={() => setEvidenceOpen(false)} onOpenOriginalReport={onOpenOriginalReport} />
    </aside>;
}
function IssueStatusTab({
  label,
  count,
  active,
  tone,
  onClick
}: {
  label: string;
  count: number;
  active: boolean;
  tone: 'red' | 'orange' | 'green';
  onClick: () => void;
}) {
  const toneClass = {
    red: active ? 'bg-background text-danger' : 'text-muted',
    orange: active ? 'bg-background text-warning' : 'text-muted',
    green: active ? 'bg-background text-success' : 'text-muted'
  }[tone];
  return <button type="button" className={`rounded-md px-2 py-2 text-center transition-colors ${toneClass}`} onClick={onClick}>
      <div className="text-[16px] font-extrabold leading-6">{count}</div>
      <div className="text-[12px] font-bold leading-5">{label}</div>
    </button>;
}
function IssueLedgerCard({
  issue,
  selected,
  onClick
}: {
  issue: HealthIssueItem;
  selected: boolean;
  onClick: () => void;
}) {
  return <button type="button" className={`w-full rounded-lg border p-3 text-left transition-colors ${selected ? 'border-success bg-success-soft' : 'border-border bg-background hover:border-success/30'}`} onClick={onClick}>
      <div className="flex items-start justify-between gap-3">
        <span className={`rounded-md px-2 py-1 text-[12px] font-extrabold ${abnormalGradeClass(issue.abnormalGrade)}`}>
          {issue.adviceLevel}
        </span>
        <span className={`shrink-0 rounded-full px-2 py-1 text-[11px] font-bold ${issueStatusClass(issue.status)}`}>
          {issueStatusLabel(issue.status)}
        </span>
      </div>
      <div className="mt-2 line-clamp-2 text-[15px] font-extrabold leading-6 text-foreground">
        {issue.title}
      </div>
      <div className="mt-1 text-[12px] font-bold leading-5 text-muted">
        {formatDate(issue.sourceDate)} · {issue.sourceName} ·{' '}
        {abnormalGradeLabel(issue.abnormalGrade)}
      </div>
      <div className="mt-2 text-[12px] font-extrabold text-foreground">
        检查项目：
      </div>
      <div className="mt-1 line-clamp-2 text-[12px] font-bold leading-5 text-muted">
        {issue.checkItems.length > 0 ? issue.checkItems.join('、') : '暂无数据'}
      </div>
      <div className="mt-2 rounded-md bg-surface-secondary px-3 py-2 text-[12px] font-bold leading-5 text-muted">
        {issue.advice}
      </div>
      <div className="mt-3 flex items-center justify-between gap-3 border-t border-border pt-2">
        <span className="inline-flex items-center gap-1 text-[12px] font-extrabold text-accent">
          <Eye className="size-3.5" />
          查看证据 {issue.evidence.length}
        </span>
        <span className="text-[11px] font-bold text-muted">点击展开证据链</span>
      </div>
    </button>;
}
function IssueEvidenceDock({
  issue,
  open,
  onClose,
  onOpenOriginalReport
}: {
  issue: HealthIssueItem | null;
  open: boolean;
  onClose: () => void;
  onOpenOriginalReport: (evidence: HealthIssueEvidence) => void;
}) {
  if (!issue || !open) return null;
  if (typeof document === 'undefined') return null;
  return createPortal(<div className="fixed inset-0 z-[9999] flex items-end bg-black/40 xl:items-stretch xl:justify-end" onClick={onClose}>
      <section className="relative z-[10000] max-h-[82vh] w-full overflow-hidden rounded-t-[18px] bg-surface-secondary shadow-[0_-16px_36px_rgba(15,23,42,0.18)] xl:h-full xl:max-h-none xl:w-[520px] xl:rounded-none xl:shadow-[-16px_0_36px_rgba(15,23,42,0.18)]" onClick={event => event.stopPropagation()}>
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-separator xl:hidden" />
        <div className="flex items-start justify-between gap-3 border-b border-border px-4 pb-3 pt-3">
          <div>
            <h2 className="m-0 text-[20px] font-extrabold leading-8">
              选中问题证据链
            </h2>
            <p className="m-0 mt-1 text-[14px] font-bold leading-6 text-muted">
              报告时间轴只服务于当前选中问题
            </p>
          </div>
          <button type="button" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-background text-muted shadow-sm" aria-label="关闭证据链" onClick={onClose}>
            <Xmark className="size-4" />
          </button>
        </div>
        <div className="max-h-[calc(82vh-94px)] overflow-y-auto px-4 pb-5 pt-4 xl:max-h-[calc(100vh-94px)]">
          <IssueEvidenceContent issue={issue} className="" onOpenOriginalReport={onOpenOriginalReport} />
        </div>
      </section>
    </div>, document.body);
}
function IssueEvidenceContent({
  issue,
  className = 'mt-4',
  onOpenOriginalReport
}: {
  issue: HealthIssueItem;
  className?: string;
  onOpenOriginalReport: (evidence: HealthIssueEvidence) => void;
}) {
  const rootClass = className ? `${className} space-y-3` : 'space-y-3';
  return <div className={rootClass}>
      <div className="rounded-lg border border-border bg-background px-3 py-2">
        <div className="line-clamp-2 text-[18px] font-extrabold leading-7 text-foreground">
          {issue.title}
        </div>
        <div className="mt-1 text-[14px] font-bold leading-6 text-muted">
          {issue.timeframe} · {issue.adviceLevel}
        </div>
      </div>
      {issue.evidence.length === 0 ? <EmptyState text="暂无可展示的证据" /> : <div className="space-y-3">
          {issue.evidence.map(evidence => <div key={evidence.id} className="grid grid-cols-[12px_minmax(0,1fr)] gap-3">
              <div className="pt-2">
                <span className={`block h-2.5 w-2.5 rounded-full ${evidence.status === 'resolved' ? 'bg-success' : evidence.status === 'pending_review' ? 'bg-warning' : 'bg-danger'}`} />
              </div>
              <div className="rounded-lg border border-border bg-background px-4 py-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="text-[15px] font-extrabold leading-6 text-foreground">
                    {formatDate(evidence.date)} · {evidence.reportName}
                  </div>
                  <button type="button" className="inline-flex shrink-0 items-center gap-1 rounded-md border border-accent/30 bg-accent-soft px-3 py-1.5 text-[13px] font-extrabold leading-5 text-accent" onClick={() => onOpenOriginalReport(evidence)}>
                    <Eye className="size-3.5" />
                    查看原报告
                  </button>
                </div>
                <div className="mt-1 text-[14px] font-bold leading-6 text-success">
                  {evidence.role}
                </div>
                {!evidence.reportId && <div className="mt-2 rounded-md bg-danger-soft px-3 py-2 text-[13px] font-bold leading-5 text-danger">
                    证据未绑定原报告，无法跳转核验
                  </div>}
                <div className="mt-1 text-[15px] font-bold leading-7 text-muted">
                  {evidence.problem}
                </div>
              </div>
            </div>)}
        </div>}
    </div>;
}
function OriginalReportDock({
  state,
  loading,
  onClose
}: {
  state: OriginalReportState | null;
  loading: boolean;
  onClose: () => void;
}) {
  if (!state) return null;
  if (typeof document === 'undefined') return null;
  const report = state.report;
  return createPortal(<div className="fixed inset-0 z-[10010] flex items-end bg-black/40 xl:items-stretch xl:justify-end" onClick={onClose}>
      <section className="relative z-[10011] max-h-[88vh] w-full overflow-hidden rounded-t-[18px] bg-surface-secondary shadow-[0_-16px_36px_rgba(15,23,42,0.18)] xl:h-full xl:max-h-none xl:w-[640px] xl:rounded-none xl:shadow-[-16px_0_36px_rgba(15,23,42,0.18)]" onClick={event => event.stopPropagation()}>
        <div className="mx-auto mt-2 h-1 w-10 rounded-full bg-separator xl:hidden" />
        <div className="flex items-start justify-between gap-3 border-b border-border bg-background px-5 pb-4 pt-4">
          <div className="min-w-0">
            <h2 className="m-0 text-[20px] font-extrabold leading-8 text-foreground">
              原报告核验
            </h2>
            <p className="m-0 mt-1 truncate text-[14px] font-bold leading-6 text-muted">
              {formatDate(state.evidence.date)} · {state.evidence.reportName}
            </p>
          </div>
          <button type="button" className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-surface-secondary text-muted shadow-sm" aria-label="关闭原报告" onClick={onClose}>
            <Xmark className="size-4" />
          </button>
        </div>
        <div className="max-h-[calc(88vh-96px)] space-y-4 overflow-y-auto px-5 pb-6 pt-4 xl:max-h-[calc(100vh-96px)]">
          <section className="rounded-lg border border-success/30 bg-success-soft px-4 py-3">
            <div className="text-[13px] font-extrabold leading-5 text-success">
              当前证据
            </div>
            <div className="mt-1 whitespace-pre-wrap text-[15px] font-bold leading-7 text-success">
              {state.evidence.problem || '暂无证据原文'}
            </div>
          </section>

          {loading && !report ? <section className="rounded-lg border border-dashed border-border bg-background px-4 py-10 text-center">
              <Spinner />
            </section> : state.error ? <section className="rounded-lg border border-danger/30 bg-danger-soft px-4 py-5 text-[15px] font-bold leading-7 text-danger">
              {state.error}
            </section> : report ? <section className="space-y-4">
              <div className="rounded-lg border border-accent/30 bg-background px-4 py-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-[18px] font-extrabold leading-7 text-foreground">
                      {report.title || '原报告'}
                    </div>
                    <div className="mt-1 text-[13px] font-bold leading-5 text-muted">
                      {formatDate(report.date)} ·{' '}
                      {originalReportSourceLabel(report.sourceType)}
                    </div>
                  </div>
                  <InfoPill tone="blue">单报告</InfoPill>
                </div>
                {report.meta.length > 0 && <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                    {report.meta.map(item => <div key={`${item.label}-${item.value}`} className="rounded-md bg-surface-secondary px-3 py-2">
                        <div className="text-[12px] font-bold leading-5 text-muted">
                          {item.label}
                        </div>
                        <div className="mt-0.5 break-words text-[14px] font-extrabold leading-6 text-foreground">
                          {item.value}
                        </div>
                      </div>)}
                  </div>}
              </div>

              {report.sections.map(section => <div key={section.label} className="rounded-lg border border-border bg-background px-4 py-3">
                  <div className="text-[14px] font-extrabold leading-6 text-foreground">
                    {section.label}
                  </div>
                  <div className="mt-1 whitespace-pre-wrap break-words text-[15px] font-bold leading-7 text-muted">
                    {section.value}
                  </div>
                </div>)}

              {report.labItems.length > 0 && <div className="rounded-lg border border-border bg-background px-4 py-3">
                  <div className="text-[14px] font-extrabold leading-6 text-foreground">
                    检验明细
                  </div>
                  <div className="mt-3 overflow-x-auto">
                    <table className="w-full min-w-[620px] border-collapse text-left text-[13px]">
                      <thead>
                        <tr className="bg-surface-secondary text-muted">
                          {['项目', '结果', '参考范围', '标志', '备注'].map(header => <th key={header} className="border border-border px-3 py-2 font-extrabold">
                                {header}
                              </th>)}
                        </tr>
                      </thead>
                      <tbody>
                        {report.labItems.map((item, index) => <tr key={`${item.name}-${index}`}>
                            <td className="border border-border px-3 py-2 font-bold text-foreground">
                              {item.name || '-'}
                            </td>
                            <td className="border border-border px-3 py-2 font-bold text-foreground">
                              {[item.result, item.unit].filter(Boolean).join(' ') || '-'}
                            </td>
                            <td className="border border-border px-3 py-2 font-bold text-foreground">
                              {item.reference || '-'}
                            </td>
                            <td className="border border-border px-3 py-2 font-bold text-foreground">
                              {item.flag || '-'}
                            </td>
                            <td className="border border-border px-3 py-2 font-bold text-foreground">
                              {item.note || '-'}
                            </td>
                          </tr>)}
                      </tbody>
                    </table>
                  </div>
                </div>}
            </section> : null}
        </div>
      </section>
    </div>, document.body);
}
function MedicalReferencePanel({
  planModule,
  module,
  group,
  snapshots,
  selectedAtomIds,
  facts,
  knowledgeVersion,
  disclaimer,
  editable,
  loading,
  getReferenceCount,
  onModuleChange,
  onToggle
}: {
  planModule: HealthPlanModule;
  module: EvidenceModule | null;
  group?: HealthPlanMedicalReferenceGroup;
  snapshots: EvidenceSnapshot[];
  selectedAtomIds: string[];
  facts: PatientEvidenceFact[];
  knowledgeVersion: string;
  disclaimer: string;
  editable: boolean;
  loading: boolean;
  getReferenceCount: (module: EvidenceModule) => number;
  onModuleChange: (module: EvidenceModule) => void;
  onToggle: (module: EvidenceModule, atomId: string) => void;
}) {
  const moduleLabels: Record<EvidenceModule, string> = {
    diet: '饮食方案',
    nutrition: '营养方案',
    exercise: '运动处方',
    monitoring: '监测方案',
    medication: '用药方案'
  };
  const modules: {
    key: EvidenceModule;
    label: string;
  }[] = [{
    key: 'diet',
    label: '饮食'
  }, {
    key: 'nutrition',
    label: '营养'
  }, {
    key: 'exercise',
    label: '运动'
  }, {
    key: 'monitoring',
    label: '监测'
  }, {
    key: 'medication',
    label: '用药'
  }];
  const selected = new Set(selectedAtomIds);
  return <section className="min-w-0 rounded-[10px] border border-border bg-surface xl:sticky xl:top-4">
      <div className="px-4 pt-4 pb-3">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h2 className="m-0 text-[16px] font-extrabold leading-6">
              医学参考
            </h2>
            <p className="m-0 mt-1 text-[12px] font-bold leading-5 text-muted">
              对照患者事实审阅已核验的指南原子
            </p>
          </div>
          <Chip size="sm" variant="soft">
            知识库 {knowledgeVersion || '—'}
          </Chip>
        </div>
      </div>

      <Tabs className="w-full" selectedKey={module ?? 'none'} variant="secondary" onSelectionChange={key => onModuleChange(key as EvidenceModule)}>
        <Tabs.ListContainer className="px-4">
          <Tabs.List aria-label="医学参考分类">
            {modules.map(item => <Tabs.Tab key={item.key} id={item.key} className="whitespace-nowrap px-2">
                {item.label}
                <span className="ml-1 tabular-nums text-muted">
                  {getReferenceCount(item.key)}
                </span>
                <Tabs.Indicator />
              </Tabs.Tab>)}
          </Tabs.List>
        </Tabs.ListContainer>

        {module === null && <div className="px-4 py-12">
            <EmptyState text={`${healthPlanModuleMeta[planModule].title}暂未配置医学参考`} />
            <p className="m-0 mt-3 text-center text-[12px] font-medium leading-5 text-muted">
              可从上方选择饮食、营养、运动、监测或用药继续审阅
            </p>
          </div>}

        {modules.map(item => <Tabs.Panel key={item.key} id={item.key} className="outline-none">
            {item.key === module && (loading ? <div className="flex justify-center py-12">
                  <Spinner />
                </div> : <ScrollShadow className="max-h-[calc(100vh-210px)] px-4 pb-4">
                  <div className="space-y-5 pt-4">
                    <section>
                      <div className="text-[14px] font-extrabold">
                        本次匹配患者事实
                      </div>
                      {facts.length === 0 ? <div className="mt-2 text-[13px] font-medium text-muted">
                          暂无可用于匹配的结构化患者事实
                        </div> : <div className="mt-3 flex flex-wrap gap-2">
                          {facts.map((fact, index) => <Chip key={`${fact.code}-${fact.value}-${index}`} size="sm" variant="soft">
                              {fact.label}：{fact.value}
                            </Chip>)}
                        </div>}
                    </section>

                    {snapshots.length > 0 && <section>
                        <div className="mb-3 flex items-center justify-between gap-3">
                          <div>
                            <div className="text-[15px] font-extrabold">
                              本版本方案依据
                            </div>
                            <div className="mt-1 text-[12px] font-medium text-muted">
                              已冻结引用来源和挂载时患者事实
                            </div>
                          </div>
                          <Chip size="sm" variant="soft">
                            {snapshots.length} 条
                          </Chip>
                        </div>
                        <div className="space-y-3">
                          {snapshots.map(snapshot => <div key={`${snapshot.module}-${snapshot.atomId}`} className="rounded-lg border border-success/40 bg-success-soft/30 p-4">
                              <div className="flex flex-wrap items-center gap-2">
                                <Chip size="sm" color="success" variant="soft">
                                  已冻结
                                </Chip>
                                <span className="text-[12px] font-bold text-muted">
                                  {snapshot.atomId} · v
                                  {snapshot.knowledgeVersion}
                                </span>
                                {!selected.has(snapshot.atomId) && <Chip size="sm" color="warning" variant="soft">
                                    保存后移除
                                  </Chip>}
                              </div>
                              <div className="mt-3 text-[14px] font-bold leading-6 text-foreground">
                                {snapshot.statement}
                              </div>
                              <div className="mt-2 text-[13px] font-medium leading-5 text-muted">
                                匹配理由：{snapshot.matchReason}
                              </div>
                              <div className="mt-2 text-[12px] font-medium leading-5 text-muted">
                                原子条件：
                                {snapshot.conditionZh || '未单独列出'}
                              </div>
                              {snapshot.exceptionsZh.length > 0 && <div className="mt-1 text-[12px] font-bold leading-5 text-warning">
                                  需排除：{snapshot.exceptionsZh.join('；')}
                                </div>}
                              <div className="mt-2 text-[12px] font-bold text-muted">
                                {snapshot.source.title}
                              </div>
                            </div>)}
                        </div>
                      </section>}

                    <section>
                      <div className="mb-3 flex flex-wrap items-end justify-between gap-3">
                        <div>
                          <div className="text-[15px] font-extrabold">
                            {moduleLabels[item.key]}参考
                          </div>
                          <div className="mt-1 text-[12px] font-medium text-muted">
                            “需复核”表示适用条件仍需人工确认
                          </div>
                        </div>
                        {group && <Chip size="sm" variant="soft">
                            {group.references.length} 条
                          </Chip>}
                      </div>
                      {!group || group.references.length === 0 ? <EmptyState text={group?.emptyReason || '暂无匹配医学参考'} /> : <div className="space-y-3">
                          {group.references.map(reference => {
                  const isSelected = selected.has(reference.atomId);
                  return <article key={reference.atomId} className={`rounded-lg border p-4 ${isSelected ? 'border-accent bg-accent-soft/30' : 'border-border bg-background'}`}>
                                <div className="flex flex-wrap items-center justify-between gap-3">
                                  <div className="flex flex-wrap items-center gap-2">
                                    <Chip size="sm" color={reference.matchStatus === 'direct' ? 'success' : 'warning'} variant="soft">
                                      {reference.matchStatus === 'direct' ? '直接匹配' : '需人工复核'}
                                    </Chip>
                                    <span className="text-[12px] font-bold text-muted">
                                      {reference.atomId} · v
                                      {reference.knowledgeVersion}
                                    </span>
                                  </div>
                                  {editable && <Button size="sm" variant={isSelected ? 'outline' : 'primary'} onPress={() => onToggle(item.key, reference.atomId)}>
                                      {isSelected ? '移出方案依据' : '加入方案依据'}
                                    </Button>}
                                </div>
                                <div className="mt-3 text-[14px] font-extrabold leading-6 text-foreground">
                                  {reference.statement}
                                </div>
                                <div className="mt-2 rounded-md bg-surface-secondary px-3 py-2 text-[13px] font-medium leading-5 text-muted">
                                  匹配理由：{reference.matchReason}
                                </div>
                                <div className="mt-2 text-[12px] font-medium leading-5 text-muted">
                                  原子条件：
                                  {reference.conditionZh || '未单独列出'}
                                </div>
                                {reference.exceptionsZh.length > 0 && <div className="mt-1 text-[12px] font-bold leading-5 text-warning">
                                    需排除：{reference.exceptionsZh.join('；')}
                                  </div>}
                                <div className="mt-3 grid gap-2 text-[12px] font-medium text-muted">
                                  <div>
                                    原始强度：
                                    {reference.grading.strengthOriginal || '未报告'}
                                  </div>
                                  <div>
                                    证据等级：
                                    {reference.grading.evidenceOriginal || '未报告'}
                                  </div>
                                  <div>来源：{reference.source.title}</div>
                                </div>
                              </article>;
                })}
                        </div>}
                    </section>

                    <Notice>
                      {disclaimer || '医学参考不替代诊断、处方或临床判断'}
                    </Notice>
                  </div>
                </ScrollShadow>)}
          </Tabs.Panel>)}
      </Tabs>
    </section>;
}
function InfoPill({
  children,
  tone = 'default'
}: {
  children: ReactNode;
  tone?: 'default' | 'blue' | 'green' | 'orange';
}) {
  const toneClass = {
    default: 'bg-background text-foreground',
    blue: 'bg-accent-soft text-accent',
    green: 'bg-success-soft text-success',
    orange: 'bg-warning-soft text-warning'
  }[tone];
  return <span className={`rounded-full px-3 py-1 text-[11px] font-extrabold ${toneClass}`}>
      {children}
    </span>;
}
function TextModuleCard({
  icon,
  title,
  tone,
  questionnaireTitle,
  items,
  editable,
  expanded,
  placeholder,
  hint,
  medicalReferenceCount,
  attachedReferenceCount,
  onOpenMedicalReference,
  onToggle,
  onAdd,
  onDelete,
  onRemoveAdded,
  onUpdate
}: {
  icon: ReactNode;
  title: string;
  tone: HealthPlanModuleTone;
  questionnaireTitle?: string;
  items: TextListEditItem[];
  editable: boolean;
  expanded: boolean;
  placeholder: string;
  hint?: string;
  onToggle?: () => void;
  onAdd: () => void;
  onDelete: (id: string) => void;
  onRemoveAdded: (id: string) => void;
  onUpdate: (id: string, value: string) => void;
} & MedicalReferenceTriggerProps) {
  const visibleItems = items.filter(item => !item.deleted);
  return <ModuleCard icon={icon} title={title} tone={tone} itemCount={visibleItems.length} expanded={expanded} questionnaireTitle={questionnaireTitle} medicalReferenceCount={medicalReferenceCount} attachedReferenceCount={attachedReferenceCount} onOpenMedicalReference={onOpenMedicalReference} onToggle={onToggle} onAdd={editable ? onAdd : undefined}>
      {visibleItems.length > 0 && <div className="mt-1 space-y-2">
          {visibleItems.map((item, idx) => <div key={item.id} className={`grid items-center gap-2 ${editable ? 'grid-cols-[32px_minmax(0,1fr)_56px]' : 'grid-cols-[32px_minmax(0,1fr)]'}`}>
              <span className="text-muted text-center text-[14px] font-extrabold">
                {idx + 1}
              </span>
              <SimpleInput value={item.text} placeholder={placeholder} isDisabled={!editable} onChange={value => onUpdate(item.id, value)} />
              {editable && <Button size="sm" variant="ghost" className="text-danger" onPress={() => item.source === 'added' ? onRemoveAdded(item.id) : onDelete(item.id)}>
                  删除
                </Button>}
            </div>)}
        </div>}
      {hint && <div className="mt-2 text-xs text-muted">{hint}</div>}
    </ModuleCard>;
}
function MonitoringModuleCard({
  monitoring,
  editable,
  expanded,
  medicalReferenceCount,
  attachedReferenceCount,
  onOpenMedicalReference,
  onToggle,
  onAddMonitoring,
  onRemoveMonitoring,
  onUpdateMonitoring
}: {
  monitoring: StructuredEditItem<MonitoringItem>[];
  editable: boolean;
  expanded: boolean;
  onToggle?: () => void;
  onAddMonitoring: () => void;
  onRemoveMonitoring: (idx: number) => void;
  onUpdateMonitoring: (idx: number, patch: Partial<MonitoringItem>) => void;
} & MedicalReferenceTriggerProps) {
  const visibleMonitoring = monitoring.filter(item => !item.deleted);
  return <ModuleCard icon={<HealthPlanModuleIcon module="monitoring" />} title={healthPlanModuleMeta.monitoring.title} tone={healthPlanModuleMeta.monitoring.tone} itemCount={visibleMonitoring.length} expanded={expanded} medicalReferenceCount={medicalReferenceCount} attachedReferenceCount={attachedReferenceCount} onOpenMedicalReference={onOpenMedicalReference} onToggle={onToggle} onAdd={editable ? onAddMonitoring : undefined}>
      {visibleMonitoring.length > 0 && <div className="flex flex-col gap-3">
          {visibleMonitoring.map((item, visibleIndex) => {
        const itemIndex = monitoring.indexOf(item);
        return <MonitoringEditor key={item.id} item={item.value} index={itemIndex} displayIndex={visibleIndex} editable={editable} onRemove={onRemoveMonitoring} onUpdate={onUpdateMonitoring} />;
      })}
        </div>}
    </ModuleCard>;
}
function ReviewReminderModuleCard({
  items,
  editable,
  expanded,
  onToggle,
  onAdd,
  onRemove,
  onUpdate,
  onReview,
  onGenerate,
  generating
}: {
  items: ReviewReminderItem[];
  editable: boolean;
  expanded: boolean;
  onToggle?: () => void;
  onAdd: () => void;
  onRemove: (idx: number) => void;
  onUpdate: (idx: number, patch: Partial<ReviewReminderItem>) => void;
  onReview: (idx: number, status: ReviewReminderItem['reviewStatus']) => void;
  onGenerate: () => void;
  generating: boolean;
}) {
  return <ModuleCard icon={<HealthPlanModuleIcon module="review" />} title={healthPlanModuleMeta.review.title} tone={healthPlanModuleMeta.review.tone} itemCount={items.length} expanded={expanded} onToggle={onToggle} onAdd={editable ? onAdd : undefined}>
      {editable && items.length === 0 && <div className="mb-3 rounded-lg border border-dashed border-warning/50 bg-warning-soft p-4">
          <div className="text-[13px] font-bold leading-5 text-muted">
            可根据患者门诊、疾病、当前用药、体检和健康关注生成首次提醒，生成后仍需人工审核
          </div>
          <Button className="mt-3" size="sm" variant="outline" isPending={generating} onPress={onGenerate}>
            AI 生成复查提醒
          </Button>
        </div>}
      {items.length === 0 ? <EmptyState text="暂无复查提醒" /> : <div className="flex flex-col gap-3">
          {items.map((item, index) => <ReviewReminderEditor key={item.id || index} item={item} index={index} editable={editable} onRemove={onRemove} onUpdate={onUpdate} onReview={onReview} />)}
        </div>}
      <div className="mt-2 text-xs text-muted">
        首版由 AI
        根据门诊、疾病、用药和体检数据生成；仅审核通过并发布的提醒会同步到患者端
      </div>
    </ModuleCard>;
}
function ReviewReminderEditor({
  item,
  index,
  editable,
  onRemove,
  onUpdate,
  onReview
}: {
  item: ReviewReminderItem;
  index: number;
  editable: boolean;
  onRemove: (idx: number) => void;
  onUpdate: (idx: number, patch: Partial<ReviewReminderItem>) => void;
  onReview: (idx: number, status: ReviewReminderItem['reviewStatus']) => void;
}) {
  const reviewStatus = item.reviewStatus || 'pending_review';
  const reviewLabel = reviewStatus === 'approved' ? '已批准' : reviewStatus === 'rejected' ? '已拒绝' : '待审核';
  return <div className="rounded-lg border border-warning/40 bg-background p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-[15px] font-extrabold text-foreground">
            复查提醒 {index + 1}
          </div>
          <Chip size="sm" color={reviewStatus === 'approved' ? 'success' : reviewStatus === 'rejected' ? 'danger' : 'warning'} variant="soft">
            {reviewLabel}
          </Chip>
        </div>
        {editable && <Dropdown>
            <Dropdown.Trigger aria-label="复查提醒操作" className="rounded-md px-2 py-1 text-[13px] font-bold text-muted hover:bg-surface-secondary hover:text-foreground">
              操作
            </Dropdown.Trigger>
            <Dropdown.Popover>
              <Dropdown.Menu>
                <Dropdown.Item onAction={() => onReview(index, 'approved')}>
                  批准
                </Dropdown.Item>
                <Dropdown.Item onAction={() => onReview(index, 'rejected')}>
                  拒绝
                </Dropdown.Item>
                <Dropdown.Item onAction={() => onRemove(index)}>
                  删除
                </Dropdown.Item>
              </Dropdown.Menu>
            </Dropdown.Popover>
          </Dropdown>}
      </div>

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <EditFieldLabel label="提醒标题">
          <SimpleInput value={item.title} placeholder="如复查糖化血红蛋白" isDisabled={!editable} isInvalid={editable && item.title.trim() === ''} onChange={value => onUpdate(index, {
          title: value
        })} />
        </EditFieldLabel>
        <EditFieldLabel label="复查分类">
          <SimpleSelect value={item.category} options={reviewReminderCategoryOptions} isDisabled={!editable} onChange={value => onUpdate(index, {
          category: value as ReviewReminderItem['category']
        })} />
        </EditFieldLabel>
        <EditFieldLabel label="建议复查日期">
          <DateField className="w-full" value={item.dueDate} isDisabled={!editable} onChange={value => onUpdate(index, {
          dueDate: value ?? ''
        })} />
        </EditFieldLabel>
        <EditFieldLabel label="建议科室">
          <SimpleInput value={item.department} placeholder="不确定时可留空" isDisabled={!editable} onChange={value => onUpdate(index, {
          department: value
        })} />
        </EditFieldLabel>
      </div>

      <EditFieldLabel className="mt-3" label="复查原因">
        <SimpleTextArea value={item.reason} placeholder="说明为什么需要复查" rows={5} isDisabled={!editable} onChange={value => onUpdate(index, {
        reason: value
      })} />
      </EditFieldLabel>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <EditFieldLabel label="复查项目（每行一项）">
          <SimpleTextArea value={(item.checkItems ?? []).join('\n')} rows={3} isDisabled={!editable} onChange={value => onUpdate(index, {
          checkItems: value.split('\n')
        })} />
        </EditFieldLabel>
        <EditFieldLabel label="复查前准备（每行一项）">
          <SimpleTextArea value={(item.preparations ?? []).join('\n')} rows={3} isDisabled={!editable} onChange={value => onUpdate(index, {
          preparations: value.split('\n')
        })} />
        </EditFieldLabel>
      </div>

      <EditFieldLabel className="mt-3" label="提醒依据">
        <SimpleMultiSelect value={item.sourceTypes ?? []} options={reviewReminderSourceOptions} isDisabled={!editable} onChange={value => onUpdate(index, {
        sourceTypes: value as ReviewReminderItem['sourceTypes']
      })} />
      </EditFieldLabel>
    </div>;
}
function RecommendationModuleCard({
  items,
  expanded,
  onToggle
}: {
  items: ProductRecommendation[];
  expanded: boolean;
  onToggle?: () => void;
}) {
  return <ModuleCard icon={<HealthPlanModuleIcon module="recommendation" />} title={healthPlanModuleMeta.recommendation.title} tone={healthPlanModuleMeta.recommendation.tone} itemCount={items.length} expanded={expanded} onToggle={onToggle}>
      {items.length === 0 ? <EmptyState text="暂无推荐产品" /> : <div className="flex flex-col gap-3">
          {items.map(item => <div key={item.id} className="rounded-lg border border-border bg-surface-secondary p-3">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[14px] font-extrabold text-foreground">
                  {item.productName}
                </span>
                {item.productBrand && <Chip size="sm" variant="soft">
                    {item.productBrand}
                  </Chip>}
                {item.productCategory && <Chip size="sm" variant="soft">
                    {item.productCategory}
                  </Chip>}
                <span className="font-extrabold text-success">
                  ¥{item.productPrice}
                </span>
              </div>
              {item.productSpec && <div className="mt-1 text-[12px] font-bold text-muted">
                  规格：{item.productSpec}
                </div>}
              <div className="mt-2 text-[13px] font-medium leading-5 text-muted">
                {item.reason || '暂无推荐理由'}
              </div>
            </div>)}
        </div>}
    </ModuleCard>;
}
function ModuleCard({
  icon,
  title,
  tone,
  itemCount,
  expanded,
  questionnaireTitle,
  medicalReferenceCount,
  attachedReferenceCount,
  onOpenMedicalReference,
  onToggle,
  onAdd,
  children
}: {
  icon: ReactNode;
  title: string;
  tone: HealthPlanModuleTone;
  itemCount: number;
  expanded: boolean;
  questionnaireTitle?: string;
  medicalReferenceCount?: number;
  attachedReferenceCount?: number;
  onOpenMedicalReference?: () => void;
  onToggle?: () => void;
  onAdd?: () => void;
  children: ReactNode;
}) {
  const headerContent = <>
      <span className={`flex size-10 shrink-0 items-center justify-center rounded-lg ${healthPlanModuleToneClass(tone)} [&>svg]:size-6`}>
        {icon}
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-[18px] font-extrabold leading-7">{title}</div>
        <div className={`mt-0.5 truncate text-[13px] font-bold leading-5 ${questionnaireTitle ? 'text-warning-soft-foreground' : 'text-muted'}`}>
          {questionnaireTitle ? `用户待完成：${questionnaireTitle}` : `共 ${itemCount} 项`}
        </div>
      </div>
      {onToggle && (expanded ? <ChevronUp className="size-5 shrink-0 text-foreground" /> : <ChevronDown className="size-5 shrink-0 text-foreground" />)}
    </>;
  return <section className="overflow-hidden rounded-xl border border-border bg-surface">
      <div className="flex items-center">
        {onToggle ? <button type="button" className="flex min-w-0 flex-1 items-center gap-4 px-4 py-3 text-left transition-colors hover:bg-surface-secondary/60 focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-accent" aria-expanded={expanded} onClick={onToggle}>
            {headerContent}
          </button> : <div className="flex min-w-0 flex-1 items-center gap-4 px-4 py-3">
            {headerContent}
          </div>}
        {onOpenMedicalReference && <div className="shrink-0 pr-4">
            <Button size="sm" variant="outline" onPress={onOpenMedicalReference}>
              医学参考 {medicalReferenceCount ?? 0}
              {(attachedReferenceCount ?? 0) > 0 ? ` · 已采用 ${attachedReferenceCount}` : ''}
            </Button>
          </div>}
      </div>
      {expanded && <div className="border-t border-border px-4 pt-3 pb-4">
          {children}
          {onAdd && <Button className="mt-3 first:mt-0" fullWidth size="sm" variant="outline" onPress={onAdd}>
              ＋ 新增一项
            </Button>}
        </div>}
    </section>;
}
function MonitoringEditor({
  item,
  index,
  displayIndex,
  editable,
  onRemove,
  onUpdate
}: {
  item: MonitoringItem;
  index: number;
  displayIndex: number;
  editable: boolean;
  onRemove: (idx: number) => void;
  onUpdate: (idx: number, patch: Partial<MonitoringItem>) => void;
}) {
  return <div className="rounded-lg border border-warning/40 bg-background p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="text-[15px] font-extrabold text-foreground">
          监测项 {displayIndex + 1}
        </div>
        {editable && <Button size="sm" variant="ghost" className="text-danger" onPress={() => onRemove(index)}>
            删除
          </Button>}
      </div>

      <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_180px]">
        <EditFieldLabel label="监测类型">
          <SimpleSelect className="w-full" value={item.type} options={monitoringTypeOptions} isDisabled={!editable} onChange={v => {
          const type = v as MonitoringItem['type'];
          const validSlots = new Set((monitoringSlotsByType[type] ?? []).map(option => option.value));
          onUpdate(index, {
            type,
            timeSlots: item.timeSlots.filter(slot => validSlots.has(slot))
          });
        }} />
        </EditFieldLabel>
        <EditFieldLabel label="监测频率">
          <SimpleSelect className="w-full" value={item.frequency} options={frequencyOptions} isDisabled={!editable} onChange={v => onUpdate(index, {
          frequency: v as MonitoringItem['frequency']
        })} />
        </EditFieldLabel>
        <EditFieldLabel label="每次提醒次数">
          <SimpleNumberInput className="w-full" value={item.frequencyCount} minValue={1} maxValue={99} isDisabled={!editable} onChange={v => onUpdate(index, {
          frequencyCount: v
        })} />
        </EditFieldLabel>
      </div>

      <EditFieldLabel className="mt-3" label="监测时段">
        <MonitoringTimeSlotSelect type={item.type} value={item.timeSlots} isDisabled={!editable} onChange={v => onUpdate(index, {
        timeSlots: v
      })} />
      </EditFieldLabel>
    </div>;
}
function EditFieldLabel({
  label,
  className,
  children
}: {
  label: string;
  className?: string;
  children: ReactNode;
}) {
  return <label className={`block min-w-0 ${className ?? ''}`}>
      <div className="mb-1 text-[13px] font-extrabold leading-5 text-muted">
        {label}
      </div>
      {children}
    </label>;
}

// 运动类型下拉里「未指定」的占位 key：taskType 存空串，而 Select 的选项 key 不能是空串
const EXERCISE_TASK_TYPE_NONE = '__none__';

// ExerciseRxCard 结构化运动处方编辑器（替代旧的运动文本方案）
function ExerciseRxCard({
  items,
  guideOptions,
  editable,
  expanded,
  questionnaireTitle,
  medicalReferenceCount,
  attachedReferenceCount,
  onOpenMedicalReference,
  onToggle,
  onAdd,
  onUpdate,
  onRemove
}: {
  items: ExerciseRxItem[];
  guideOptions: ExerciseGuideOption[];
  editable: boolean;
  expanded: boolean;
  questionnaireTitle?: string;
  onToggle?: () => void;
  onAdd: () => void;
  onUpdate: (idx: number, patch: Partial<ExerciseRxItem>) => void;
  onRemove: (idx: number) => void;
} & MedicalReferenceTriggerProps) {
  const taskTypeOptions = [{
    value: EXERCISE_TASK_TYPE_NONE,
    label: '未指定类型'
  }, ...guideOptions.map(guide => ({
    value: guide.code,
    label: guide.title
  }))];
  const guideCodes = new Set(guideOptions.map(guide => guide.code));
  return <ModuleCard icon={<HealthPlanModuleIcon module="exercise" />} title={healthPlanModuleMeta.exercise.title} tone={healthPlanModuleMeta.exercise.tone} itemCount={items.length} expanded={expanded} questionnaireTitle={questionnaireTitle} medicalReferenceCount={medicalReferenceCount} attachedReferenceCount={attachedReferenceCount} onOpenMedicalReference={onOpenMedicalReference} onToggle={onToggle} onAdd={editable ? onAdd : undefined}>
      {items.length > 0 && <div className="flex flex-col gap-3">
          {items.map((item, idx) => <div key={idx} className="rounded-lg border border-success/30 bg-background p-3">
              <div className="grid grid-cols-1 gap-2 md:grid-cols-3">
                <SimpleInput value={item.title} placeholder="运动名称，如快走、太极" isDisabled={!editable} onChange={v => onUpdate(idx, {
            title: v
          })} />
                {/* 存量的自由文本类型不在指导库里，一律显示为未指定，保存时服务端也会置空 */}
                <SimpleSelect className="w-full" aria-label="运动类型" value={guideCodes.has(item.taskType) ? item.taskType : EXERCISE_TASK_TYPE_NONE} options={taskTypeOptions} isDisabled={!editable} onChange={v => onUpdate(idx, {
            taskType: v === EXERCISE_TASK_TYPE_NONE ? '' : v
          })} />
                <div className="flex items-center gap-2">
                  <SimpleSelect className="flex-1" value={item.frequency} options={frequencyOptions} isDisabled={!editable} onChange={v => {
              const frequency = v as ExerciseRxItem['frequency'];
              onUpdate(idx, {
                frequency,
                ...(frequency === 'daily' ? {
                  frequencyCount: 1
                } : {})
              });
            }} />
                  <SimpleNumberInput className="w-[120px]" value={item.frequencyCount} minValue={1} maxValue={99} isDisabled={!editable || item.frequency === 'daily'} onChange={v => onUpdate(idx, {
              frequencyCount: v
            })} />
                  <span className="text-muted shrink-0 text-sm">
                    {item.frequency === 'daily' ? '次/天' : '次/周'}
                  </span>
                </div>
              </div>
              <SimpleTextArea className="mt-2" value={item.description} placeholder="针对本患者的注意事项：强度/时长/禁忌，一两句话；做法由运动指导库提供" rows={2} isDisabled={!editable} onChange={v => onUpdate(idx, {
          description: v
        })} />
              {editable && <div className="mt-2 flex justify-end">
                  <Button size="sm" variant="ghost" className="text-danger" onPress={() => onRemove(idx)}>
                    删除项
                  </Button>
                </div>}
            </div>)}
        </div>}
    </ModuleCard>;
}

// region 用药模块

// 用药的真源是患者用药清单 (patient_medication), 医生在这里的每一步改动都直接落库、即时生效,
// 不跟着方案的「保存草稿」和「发布」走; 版本里的 medication 只是发布时抄的只读快照

// MedicationDraft 编辑中的一条用药字段, 医生添加行的修改与手填新增共用
type MedicationDraft = MedicationFieldsInput & {
  reminderEnabled: boolean;
};
function draftFromItem(item: PatientMedicationItem): MedicationDraft {
  return {
    drugName: item.drugName,
    specification: item.specification,
    route: item.route,
    dosage: item.dosage,
    dosageUnit: item.dosageUnit,
    frequency: item.frequency,
    mealRelation: item.mealRelation,
    doses: item.doses.map(dose => ({
      ...dose
    })),
    startDate: item.startDate,
    endDate: item.endDate,
    reminderEnabled: item.reminderEnabled
  };
}
function emptyMedicationDraft(): MedicationDraft {
  return {
    drugName: '',
    specification: '',
    route: '',
    dosage: '',
    dosageUnit: '',
    frequency: '',
    mealRelation: '',
    doses: [],
    startDate: dayjs().format('YYYY-MM-DD'),
    endDate: '',
    reminderEnabled: false
  };
}

// medicationDraftEqual 判断表单有没有改动; 剂次只比时间与用量, id 是身份不是设置
function medicationDraftEqual(a: MedicationDraft, b: MedicationDraft) {
  const normalize = (draft: MedicationDraft) => JSON.stringify({
    ...draft,
    doses: draft.doses.map(dose => [dose.time, dose.dosage ?? '', dose.dosageUnit ?? ''])
  });
  return normalize(a) === normalize(b);
}

// medicationCourseDays 起止日期算出的用药天数, 长期为 0
function medicationCourseDays(startDate: string, endDate: string) {
  if (!startDate || !endDate) return 0;
  return Math.max(0, dayjs(endDate).diff(dayjs(startDate), 'day') + 1);
}

// medicationEndDateOf 用药天数换算结束日期
function medicationEndDateOf(startDate: string, days: number) {
  return dayjs(startDate).add(Math.max(1, days) - 1, 'day').format('YYYY-MM-DD');
}

// validateMedicationDraft 保存前的前端校验, 服务端还有一道兜底
function validateMedicationDraft(draft: MedicationDraft, reviewStatus: MedicationReviewStatus): string | null {
  if (draft.drugName.trim() === '') return '请填写药品名称';
  if (!draft.startDate) return '请填写开始日期';
  if (draft.endDate && draft.endDate < draft.startDate) {
    return '结束日期不能早于开始日期';
  }
  if (reviewStatus === 'approved' && draft.doses.length === 0) {
    return '已批准的用药请排出服药时间，或选择按需服用';
  }
  if (draft.reminderEnabled && medicationDoseTimes(draft.doses).length === 0) {
    return '已开启提醒的用药请至少设置一个服药时间';
  }
  return null;
}
const medicationReviewLabel: Record<MedicationReviewStatus, string> = {
  pending_review: '待审核',
  approved: '已批准',
  rejected: '已拒绝',
  '': ''
};
function MedicationModuleCard({
  planId,
  patientId,
  editing,
  canEdit,
  expanded,
  medicalReferenceCount,
  attachedReferenceCount,
  onOpenMedicalReference,
  onToggle
}: {
  planId: string;
  patientId: string;
  // 方案处于编辑态 (负责人已进入编辑)
  editing: boolean;
  // 当前账号是专科医生
  canEdit: boolean;
  expanded: boolean;
  onToggle?: () => void;
} & MedicalReferenceTriggerProps) {
  const overviewQuery = usePatientMedicationListQuery(patientId);
  const overview = overviewQuery.data;
  const hisRxEnabled = overview?.hisRxEnabled ?? true;
  const editable = editing && canEdit && hisRxEnabled;
  const items = overview?.items ?? [];
  const doctorItems = items.filter(item => item.addedBy === 'doctor' && !item.archivedAt);
  const archivedItems = items.filter(item => item.addedBy === 'doctor' && !!item.archivedAt);
  const candidates = overview?.candidates ?? [];
  const [adding, setAdding] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const addFromHisMutation = useMedicationAddFromHisMutation(patientId);
  const reorderMutation = useMedicationReorderMutation(patientId);
  const restoreMutation = useMedicationRestoreMutation(patientId);
  const moveItem = async (index: number, direction: -1 | 1) => {
    const target = index + direction;
    if (target < 0 || target >= doctorItems.length) return;
    const ids = doctorItems.map(item => item.id);
    [ids[index], ids[target]] = [ids[target], ids[index]];
    try {
      await reorderMutation.mutateAsync({
        planId,
        ids
      });
    } catch (error) {
      notify.warning(error instanceof Error ? error.message : '调整顺序失败');
    }
  };
  const addFromHis = async (hisRxId: string) => {
    try {
      await addFromHisMutation.mutateAsync({
        planId,
        hisRxId
      });
      notify.success('已加入待审核用药');
    } catch (error) {
      notify.warning(error instanceof Error ? error.message : '加入处方失败');
    }
  };
  const restore = async (id: string) => {
    try {
      await restoreMutation.mutateAsync({
        planId,
        id
      });
      notify.success('已恢复');
    } catch (error) {
      notify.warning(error instanceof Error ? error.message : '恢复失败');
    }
  };
  return <ModuleCard icon={<HealthPlanModuleIcon module="medication" />} title={healthPlanModuleMeta.medication.title} tone={healthPlanModuleMeta.medication.tone} itemCount={doctorItems.length} expanded={expanded} medicalReferenceCount={medicalReferenceCount} attachedReferenceCount={attachedReferenceCount} onOpenMedicalReference={onOpenMedicalReference} onToggle={onToggle}>
      {overviewQuery.isLoading && <div className="mb-3">
          <Notice>正在读取患者用药清单与 HIS 处方</Notice>
        </div>}
      {overviewQuery.isError && <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1">
            <Notice tone="warning">
              {overviewQuery.error instanceof Error ? overviewQuery.error.message : '用药清单读取失败'}
            </Notice>
          </div>
          <Button size="sm" variant="outline" onPress={() => overviewQuery.refetch()}>
            重试
          </Button>
        </div>}
      {editing && !canEdit && hisRxEnabled && <div className="mb-3">
          <Notice>用药仅专科医生可调整，当前账号仅可查看</Notice>
        </div>}
      {/* 开关关闭的提示不限编辑态, 查看态也要让医生知道功能已停用 */}
      {!hisRxEnabled && overview && <div className="mb-3">
          <Notice tone="warning">
            医生用药管理功能已关闭：用药仅可查看，不能加入处方、手填、调整或审核；患者端的用药与打卡不受影响
          </Notice>
        </div>}
      {editable && <div className="mb-3">
          <Notice>
            用药的改动保存后即时对患者生效，不需要发布方案；批准后的用药患者才能看到并打卡
          </Notice>
        </div>}

      {editable && candidates.length > 0 && <MedicationCandidateList candidates={candidates} busy={addFromHisMutation.isPending} onAdd={addFromHis} />}

      {doctorItems.length > 0 && <div className="flex flex-col gap-3">
          {doctorItems.map((item, idx) => <MedicationEditor key={item.id} item={item} index={idx} itemCount={doctorItems.length} planId={planId} patientId={patientId} editable={editable} onMove={moveItem} />)}
        </div>}
      {doctorItems.length === 0 && overview && <EmptyState text={editable ? '暂无医生用药，可从 HIS 处方加入或手填添加' : '暂无医生用药'} />}

      {editable && !adding && <div className="mt-3">
          <Button size="sm" variant="outline" onPress={() => setAdding(true)}>
            手填添加用药
          </Button>
        </div>}
      {editable && adding && <NewMedicationForm planId={planId} patientId={patientId} onDone={() => setAdding(false)} onCancel={() => setAdding(false)} />}

      {archivedItems.length > 0 && <div className="mt-3">
          <button type="button" className="text-muted text-[12px] font-bold hover:text-foreground" onClick={() => setShowArchived(value => !value)}>
            {showArchived ? '收起' : '查看'}已停用的用药（{archivedItems.length}
            ）
          </button>
          {showArchived && <div className="mt-2 flex flex-col gap-2">
              {archivedItems.map(item => <div key={item.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-surface-secondary px-3 py-2">
                  <div className="min-w-0">
                    <span className="text-foreground text-[13px] font-bold">
                      {item.drugName}
                    </span>
                    <span className="text-muted ml-2 text-[12px]">
                      {item.specification} · 停用于{' '}
                      {dayjs(item.archivedAt).format('YYYY-MM-DD')}
                    </span>
                  </div>
                  {editable && <Button size="sm" variant="ghost" isDisabled={restoreMutation.isPending} onPress={() => restore(item.id)}>
                      恢复
                    </Button>}
                </div>)}
            </div>}
        </div>}

      {/* 患者在小程序自行添加的药只读展示, 让医生知道患者还在吃什么 */}
      <PatientSelfMedicationList accountId={patientId} />
    </ModuleCard>;
}

// MedicationCandidateList 还没加入清单的 HIS 处方, 点「加入」进待审核
function MedicationCandidateList({
  candidates,
  busy,
  onAdd
}: {
  candidates: MedicationHisCandidate[];
  busy: boolean;
  onAdd: (hisRxId: string) => void;
}) {
  return <div className="mb-3 rounded-lg border border-warning/40 bg-warning-soft/20 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <div className="text-foreground text-[13px] font-extrabold">
          HIS 处方（未加入）
        </div>
        <Chip size="sm" variant="soft">
          {candidates.length}
        </Chip>
        <span className="text-muted text-[12px]">
          疗程未结束或未来开始的门诊处方，加入后进入待审核
        </span>
      </div>
      <div className="flex flex-col gap-2">
        {candidates.map(candidate => <div key={candidate.hisRxId} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-border bg-background px-3 py-2">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-foreground text-[14px] font-bold">
                  {candidate.genericName}
                </span>
                {candidate.specification && <span className="text-muted text-[12px]">
                    {candidate.specification}
                  </span>}
              </div>
              <div className="text-muted mt-0.5 text-[12px]">
                {[candidate.route, `${candidate.dosage}${candidate.dosageUnit}`.trim() ? `单次 ${candidate.dosage}${candidate.dosageUnit}` : '', candidate.frequency, `${candidate.startDate} 起 ${candidate.days} 天`].filter(Boolean).join(' · ')}
              </div>
            </div>
            <Button size="sm" variant="outline" isDisabled={busy} onPress={() => onAdd(candidate.hisRxId)}>
              加入
            </Button>
          </div>)}
      </div>
    </div>;
}

// MedicationEditor 一条医生添加的用药: 本地草稿编辑, 「保存」或审核动作时一次提交
function MedicationEditor({
  item,
  index,
  itemCount,
  planId,
  patientId,
  editable,
  onMove
}: {
  item: PatientMedicationItem;
  index: number;
  itemCount: number;
  planId: string;
  patientId: string;
  editable: boolean;
  onMove: (idx: number, direction: -1 | 1) => void;
}) {
  const [draft, setDraft] = useState<MedicationDraft>(() => draftFromItem(item));
  // 服务端行更新 (别人改了、保存成功) 后用新值重新装表单
  const [seededAt, setSeededAt] = useState(item.updatedAt);
  if (seededAt !== item.updatedAt) {
    setSeededAt(item.updatedAt);
    setDraft(draftFromItem(item));
  }
  const dirty = !medicationDraftEqual(draft, draftFromItem(item));
  const saveMutation = useMedicationSaveMutation(patientId);
  const resetHisMutation = useMedicationResetHisMutation(patientId);
  const archiveMutation = useMedicationArchiveMutation(patientId);
  const busy = saveMutation.isPending || resetHisMutation.isPending || archiveMutation.isPending;
  const reviewStatus = item.reviewStatus || 'pending_review';
  const locked = item.origin === 'his';
  // 微调对照: 任一可调字段与 HIS 原值不同则展示原处方
  const hisOriginal = item.hisOriginal;
  const adjusted = hisOriginal && (hisOriginal.dosage !== item.dosage || hisOriginal.dosageUnit !== item.dosageUnit || hisOriginal.frequency !== item.frequency || hisOriginal.days !== item.days);
  const submit = async (status: Exclude<MedicationReviewStatus, ''>) => {
    const problem = validateMedicationDraft(draft, status);
    if (problem) {
      notify.warning(problem);
      return;
    }
    try {
      await saveMutation.mutateAsync({
        planId,
        id: item.id,
        ...draft,
        reviewStatus: status
      });
      notify.success(status === reviewStatus ? '已保存' : `已${medicationReviewLabel[status].replace('已', '')}`);
    } catch (error) {
      notify.warning(error instanceof Error ? error.message : '保存失败');
    }
  };
  const resetHis = async () => {
    try {
      await resetHisMutation.mutateAsync({
        planId,
        id: item.id
      });
      notify.success('已按新处方重置，请重新审核');
    } catch (error) {
      notify.warning(error instanceof Error ? error.message : '重置失败');
    }
  };
  const archive = async () => {
    try {
      await archiveMutation.mutateAsync({
        planId,
        id: item.id
      });
      notify.success('已停用');
    } catch (error) {
      notify.warning(error instanceof Error ? error.message : '停用失败');
    }
  };
  return <div className="rounded-lg border border-warning/40 bg-background p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <div className="text-[15px] font-extrabold text-foreground">
            用药项 {index + 1}
          </div>
          <Chip size="sm" variant="soft">
            {locked ? 'HIS 处方' : '医生手填'}
          </Chip>
          {adjusted && <Chip size="sm" color="warning" variant="soft">
              已微调
            </Chip>}
          <Chip size="sm" color={reviewStatus === 'approved' ? 'success' : reviewStatus === 'rejected' ? 'danger' : 'warning'} variant="soft">
            {medicationReviewLabel[reviewStatus]}
          </Chip>
          {dirty && <Chip size="sm" color="accent" variant="soft">
              未保存
            </Chip>}
        </div>
        {editable && <div className="flex flex-wrap items-center justify-end gap-1">
            <Button size="sm" variant="ghost" isDisabled={index === 0 || busy} onPress={() => onMove(index, -1)}>
              上移
            </Button>
            <Button size="sm" variant="ghost" isDisabled={index === itemCount - 1 || busy} onPress={() => onMove(index, 1)}>
              下移
            </Button>
            <Button size="sm" variant="outline" isDisabled={!dirty || busy} onPress={() => submit(reviewStatus)}>
              保存
            </Button>
            <Dropdown>
              <Dropdown.Trigger aria-label="用药操作" className="rounded-md px-2 py-1 text-[13px] font-bold text-muted hover:bg-surface-secondary hover:text-foreground">
                操作
              </Dropdown.Trigger>
              <Dropdown.Popover>
                <Dropdown.Menu>
                  <Dropdown.Item onAction={() => submit('approved')}>
                    批准
                  </Dropdown.Item>
                  <Dropdown.Item onAction={() => submit('rejected')}>
                    拒绝
                  </Dropdown.Item>
                  <Dropdown.Item onAction={() => submit('pending_review')}>
                    退回待审核
                  </Dropdown.Item>
                  <Dropdown.Item onAction={archive}>停用</Dropdown.Item>
                </Dropdown.Menu>
              </Dropdown.Popover>
            </Dropdown>
          </div>}
      </div>

      {item.hisChanged && item.hisLatest && <div className="mb-3 flex flex-wrap items-center gap-2">
          <div className="min-w-0 flex-1">
            <Notice tone="warning">
              HIS 处方已更新：{item.hisLatest.frequency || '-'} · 单次{' '}
              {item.hisLatest.dosage || '-'}
              {item.hisLatest.dosageUnit} · {item.hisLatest.days} 天
              {editable ? '，可按新处方重置（微调清空、回到待审核）' : ''}
            </Notice>
          </div>
          {editable && <Button size="sm" variant="outline" isDisabled={busy} onPress={resetHis}>
              按新处方重置
            </Button>}
        </div>}

      <MedicationFieldsEditor draft={draft} locked={locked} editable={editable} onUpdate={patch => setDraft(current => ({
      ...current,
      ...patch
    }))} />

      {adjusted && hisOriginal && <div className="mt-3 rounded-md bg-surface-secondary px-3 py-2 text-[12px] text-muted">
          原处方：{hisOriginal.frequency || '-'} · 单次{' '}
          {hisOriginal.dosage || '-'}
          {hisOriginal.dosageUnit} · {hisOriginal.days} 天
        </div>}
    </div>;
}

// NewMedicationForm 专科医生手填新增一条用药, 保存后进待审核
function NewMedicationForm({
  planId,
  patientId,
  onDone,
  onCancel
}: {
  planId: string;
  patientId: string;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [draft, setDraft] = useState<MedicationDraft>(emptyMedicationDraft);
  const addMutation = useMedicationAddMutation(patientId);
  const submit = async () => {
    const problem = validateMedicationDraft(draft, 'pending_review');
    if (problem) {
      notify.warning(problem);
      return;
    }
    try {
      await addMutation.mutateAsync({
        planId,
        ...draft
      });
      notify.success('已添加，请审核后生效');
      onDone();
    } catch (error) {
      notify.warning(error instanceof Error ? error.message : '添加失败');
    }
  };
  return <div className="mt-3 rounded-lg border border-dashed border-warning/60 bg-background p-4">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="text-[15px] font-extrabold text-foreground">
          手填添加用药
        </div>
        <div className="flex items-center gap-1">
          <Button size="sm" variant="ghost" onPress={onCancel}>
            取消
          </Button>
          <Button size="sm" variant="outline" isDisabled={addMutation.isPending} onPress={submit}>
            保存
          </Button>
        </div>
      </div>
      <MedicationFieldsEditor draft={draft} locked={false} editable onUpdate={patch => setDraft(current => ({
      ...current,
      ...patch
    }))} />
    </div>;
}

// MedicationFieldsEditor 用药的全部字段: 药品信息、频次与剂量、疗程、服药安排
//
// locked 是 HIS 处方来源: 名称 / 规格 / 途径 / 开始日期锁定, 疗程按「用药天数」微调; 手填的全部可改, 疗程按结束日期 (空为长期)
function MedicationFieldsEditor({
  draft,
  locked,
  editable,
  onUpdate
}: {
  draft: MedicationDraft;
  locked: boolean;
  editable: boolean;
  onUpdate: (patch: Partial<MedicationDraft>) => void;
}) {
  const days = medicationCourseDays(draft.startDate, draft.endDate);
  return <>
      {/* 名称/规格/途径/开始日期以 HIS 处方为准, 医生只能微调频次/单次剂量/剂量单位/用药天数与服药安排 */}
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <EditFieldLabel label="药品通用名">
          <SimpleInput value={draft.drugName} placeholder={locked ? '以 HIS 处方为准' : '请填写药品通用名'} isDisabled={!editable || locked} isInvalid={editable && !locked && draft.drugName.trim() === ''} onChange={value => onUpdate({
          drugName: value
        })} />
        </EditFieldLabel>
        <EditFieldLabel label="药品规格">
          <SimpleInput value={draft.specification} placeholder={locked ? '以 HIS 处方为准' : '如 100mg×30片'} isDisabled={!editable || locked} onChange={value => onUpdate({
          specification: value
        })} />
        </EditFieldLabel>
        <EditFieldLabel label="给药途径">
          <SimpleInput value={draft.route} placeholder={locked ? '以 HIS 处方为准' : '如 口服'} isDisabled={!editable || locked} onChange={value => onUpdate({
          route: value
        })} />
        </EditFieldLabel>
        <EditFieldLabel label="用药频次">
          <SimpleSelect value={draft.frequency} placeholder="请选择用药频次" isDisabled={!editable} options={draft.frequency && !medicationFrequencyOptions.some(option => option.value === draft.frequency) ? [{
          label: `原频次（${draft.frequency}）`,
          value: draft.frequency
        }, ...medicationFrequencyOptions] : medicationFrequencyOptions} onChange={value => {
          // 还没排过剂次时按频次自动填建议值; 排过的只按新频次的次数增删,
          // 保留医生已选的时间和它们的剂次 id, 改频次不该让患者当天已服的记录作废
          if (draft.doses.length === 0) {
            onUpdate({
              frequency: value,
              ...medicationReminderDefaults(value)
            });
            return;
          }
          const count = medicationReminderTimesCount(value);
          if (count === 0 || isMedicationAsNeeded(draft.doses)) {
            onUpdate({
              frequency: value
            });
            return;
          }
          const kept = draft.doses.slice(0, count);
          const keptTimes = kept.map(dose => dose.time);
          const padding = medicationReminderDefaults(value).doses.filter(dose => !keptTimes.includes(dose.time)).slice(0, count - kept.length);
          onUpdate({
            frequency: value,
            doses: [...kept, ...padding].sort((a, b) => a.time.localeCompare(b.time))
          });
        }} />
        </EditFieldLabel>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-3">
        <EditFieldLabel label="单次剂量">
          <SimpleInput value={draft.dosage} placeholder="如 0.5" isDisabled={!editable} onChange={value => onUpdate({
          dosage: value
        })} />
        </EditFieldLabel>
        <EditFieldLabel label="剂量单位">
          <SimpleInput value={draft.dosageUnit} placeholder="如 g、mg、片" isDisabled={!editable} onChange={value => onUpdate({
          dosageUnit: value
        })} />
        </EditFieldLabel>
        {locked ? <EditFieldLabel label="用药天数">
            <SimpleNumberInput className="w-full" value={days} minValue={1} maxValue={3650} isDisabled={!editable} onChange={value => onUpdate({
          endDate: medicationEndDateOf(draft.startDate, value)
        })} />
          </EditFieldLabel> : <EditFieldLabel label="结束日期（留空为长期）">
            <DateField className="w-full" value={draft.endDate || null} isDisabled={!editable} onChange={value => onUpdate({
          endDate: value ?? ''
        })} />
          </EditFieldLabel>}
      </div>

      <EditFieldLabel className="mt-3" label="疗程开始日期">
        <DateField className="w-full" value={draft.startDate || null} isDisabled={!editable || locked} onChange={value => onUpdate({
        startDate: value ?? ''
      })} />
      </EditFieldLabel>

      <MedicationReminderEditor draft={draft} editable={editable} onUpdate={onUpdate} />
    </>;
}

// MedicationReminderEditor 服药安排: 剂次 (时间点 + 每次用量)、提醒开关、与餐关系
//
// 剂次是打卡的单位, 每条都带着自己的 id: 编辑时原样带回服务端, 医生改了时间点患者当天已服的记录也不会丢
// 时间点个数跟着频次走: BID 就显示 2 个时间下拉框, 只显示纯时刻, 餐前餐后由「与餐关系」表达
// 频次无法按天换算 (Q6H/PRN/QW 等) 时次数不定, 才允许医生自由增删; 已有时间多于频次次数时也允许删掉多余的
// 每次用量留空表示沿用该药的单次剂量, 只有分时剂量 (早 1 片晚 2 片) 才逐剂次填
// 提醒开关医生与患者共用一个值: 患者在小程序关掉的, 医生这里看到的就是关着的
function MedicationReminderEditor({
  draft,
  editable,
  onUpdate
}: {
  draft: MedicationDraft;
  editable: boolean;
  onUpdate: (patch: Partial<MedicationDraft>) => void;
}) {
  const doses = draft.doses;
  const asNeeded = isMedicationAsNeeded(doses);
  const times = medicationDoseTimes(doses);
  const setDoses = (next: MedicationDose[]) => onUpdate({
    doses: [...next].sort((a, b) => a.time.localeCompare(b.time))
  });
  const requiredCount = medicationReminderTimesCount(draft.frequency);
  // 次数已知: 固定显示 N 个槽位 (不足的留空待选); 次数不定: 已有时间之外多一个「添加时间」槽位
  const slotCount = requiredCount > 0 ? Math.max(requiredCount, doses.length) : doses.length + 1;
  const removable = editable && doses.length > requiredCount;
  return <div className="mt-3 rounded-lg border border-border bg-surface-secondary p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-[13px] font-extrabold text-foreground">
          服药安排
        </div>
        <Switch isSelected={draft.reminderEnabled} isDisabled={!editable || asNeeded} onChange={value => onUpdate({
        reminderEnabled: value
      })}>
          <Switch.Content>
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
            <span className="text-[12px] font-bold text-muted">
              {draft.reminderEnabled ? '提醒已开启' : '提醒未开启'}
            </span>
          </Switch.Content>
        </Switch>
      </div>
      {editable && <div className="mt-3 inline-flex rounded-lg border border-border bg-background p-0.5">
          {[{
        label: '按固定时间',
        value: false
      }, {
        label: '按需服用',
        value: true
      }].map(option => <button key={option.label} type="button" className={option.value === asNeeded ? 'rounded-md bg-surface-secondary px-3 py-1 text-[12px] font-extrabold text-foreground' : 'rounded-md px-3 py-1 text-[12px] font-bold text-muted hover:text-foreground'} onClick={() => {
        if (option.value === asNeeded) return;
        if (option.value) {
          // 按需服用没有时间点可提醒, 开关一并关掉
          onUpdate({
            doses: [{
              time: ''
            }],
            reminderEnabled: false
          });
          return;
        }
        onUpdate(medicationReminderDefaults(draft.frequency));
      }}>
              {option.label}
            </button>)}
        </div>}
      {asNeeded ? <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_200px]">
          <div className="text-[12px] font-bold text-muted">
            不设固定服药时间，患者按需服用后自行记录；这类用药不发提醒、也不计入服药依从率
          </div>
          <MedicationMealRelationField draft={draft} editable={editable} onUpdate={onUpdate} />
        </div> : <div className="mt-3 grid grid-cols-1 gap-3 md:grid-cols-[1fr_200px]">
          {/* 时间块不用 EditFieldLabel: label 元素会把点击转发给内部控件, 多个下拉框会互相抢焦点 */}
          <div className="min-w-0">
            <div className="mb-1 text-[13px] font-extrabold leading-5 text-muted">
              服药时间（半小时粒度）与每次用量
            </div>
            <div className="flex flex-col gap-2">
              {Array.from({
            length: slotCount
          }, (_, slotIndex) => {
            const dose = doses[slotIndex];
            const current = dose?.time ?? '';
            // 空槽位对应「添加时间」, 非编辑态下不显示空槽位
            if (!dose && !editable) return null;
            return <div key={dose?.id || current || `empty-${slotIndex}`} className="flex flex-wrap items-center gap-1">
                    <SimpleSelect className="w-28" value={current} placeholder={requiredCount > 0 ? '选择时间' : '添加时间'} isDisabled={!editable}
              // 其他槽位已选的时刻不再列出, 避免两个槽位选成同一时间被合并
              options={medicationReminderTimeOptions.filter(option => option.value === current || !times.includes(option.value))} onChange={value => setDoses(dose ? doses.map((existing, i) => i === slotIndex ? {
                ...existing,
                time: value
              } : existing) : [...doses, {
                time: value
              }])} />
                    {dose && <>
                        <SimpleInput className="w-24" value={dose.dosage ?? ''} placeholder={draft.dosage || '单次剂量'} isDisabled={!editable} onChange={value => setDoses(doses.map((existing, i) => i === slotIndex ? {
                  ...existing,
                  dosage: value
                } : existing))} />
                        <SimpleInput className="w-20" value={dose.dosageUnit ?? ''} placeholder={draft.dosageUnit || '单位'} isDisabled={!editable || !dose.dosage} onChange={value => setDoses(doses.map((existing, i) => i === slotIndex ? {
                  ...existing,
                  dosageUnit: value
                } : existing))} />
                      </>}
                    {dose && removable && <button type="button" className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-muted hover:bg-background hover:text-foreground" aria-label={`删除服药时间 ${current || slotIndex + 1}`} onClick={() => setDoses(doses.filter((_, i) => i !== slotIndex))}>
                        <Xmark className="size-3.5" />
                      </button>}
                  </div>;
          })}
              {!editable && doses.length === 0 && <span className="text-[12px] font-bold text-muted">未设置</span>}
            </div>
            <div className="mt-1 text-[12px] font-bold text-muted">
              每次用量留空即沿用上面的单次剂量，只有早晚吃的量不一样时才逐次填写
            </div>
            {requiredCount === 0 && draft.frequency && editable && <div className="mt-1 text-[12px] font-bold text-muted">
                该频次无法按天换算服药次数，请按需添加时间
              </div>}
            {doses.length === 0 && <div className="mt-1 text-[12px] font-bold text-danger">
                尚未安排服药时间，批准前请排出时间或改为按需服用
              </div>}
            {draft.reminderEnabled && times.length === 0 && doses.length > 0 && <div className="mt-1 text-[12px] font-bold text-danger">
                  已开启提醒但未设置时间
                </div>}
          </div>
          <MedicationMealRelationField draft={draft} editable={editable} onUpdate={onUpdate} />
        </div>}
    </div>;
}

// MedicationMealRelationField 与餐关系下拉, 按固定时间与按需服用两种布局共用
function MedicationMealRelationField({
  draft,
  editable,
  onUpdate
}: {
  draft: MedicationDraft;
  editable: boolean;
  onUpdate: (patch: Partial<MedicationDraft>) => void;
}) {
  return <EditFieldLabel label="与餐关系">
      <SimpleSelect value={draft.mealRelation} placeholder="未指定" isDisabled={!editable} options={medicationMealRelationOptions} onChange={value => onUpdate({
      mealRelation: value as MedicationDraft['mealRelation']
    })} />
    </EditFieldLabel>;
}

// endregion

function EmptyState({
  text
}: {
  text: string;
}) {
  return <div className="rounded-lg border border-dashed border-border bg-background px-3 py-4 text-center text-[12px] font-bold text-muted">
      {text}
    </div>;
}
function Notice({
  children,
  tone = 'default'
}: {
  children: ReactNode;
  tone?: 'default' | 'warning';
}) {
  const cls = tone === 'warning' ? 'border-warning/40 bg-warning-soft text-warning' : 'border-border bg-surface-secondary text-muted';
  return <div className={`rounded-lg border px-4 py-3 text-[13px] font-bold ${cls}`}>
      {children}
    </div>;
}
function composeTextList(items: TextListEditItem[]) {
  return items.filter(item => !item.deleted && item.text.trim().length > 0).map(item => item.text);
}
function composeStructuredList<T>(items: StructuredEditItem<T>[]) {
  return items.filter(item => !item.deleted).map(item => item.value);
}
function formatDate(value?: string | null) {
  return value ? dayjs(value).format('YYYY-MM-DD') : '暂无数据';
}
function formatDateTime(value?: string | null) {
  return value ? dayjs(value).format('YYYY-MM-DD HH:mm') : '暂无数据';
}
function issueLedgerStateText(state?: HealthIssueLedgerState) {
  if (!state) return '正在读取健康关注缓存';
  if (state.status === 'idle') {
    return '暂无健康关注缓存，可点击刷新';
  }
  if (state.status === 'running' && state.stale) {
    return '后台刷新中，当前先展示上一版健康关注';
  }
  if (state.status === 'running') {
    return '健康关注生成中，完成后自动更新';
  }
  if (state.status === 'failed') {
    return state.generatedAt ? `刷新失败，当前展示 ${formatDateTime(state.generatedAt)} 的上一版健康关注` : '健康关注生成失败，可点击刷新重试';
  }
  return `健康关注生成于 ${formatDateTime(state.generatedAt)}`;
}
function originalReportSourceLabel(value: string) {
  const labels: Record<string, string> = {
    peis_exam: '体检记录',
    his_lab: '检验报告',
    his_exam: '检查报告',
    his_pathology: '病理报告'
  };
  return labels[value] ?? value;
}
function issueStatusLabel(status: HealthIssueStatus) {
  const labels: Record<HealthIssueStatus, string> = {
    current: '当前异常',
    pending_review: '待复查',
    resolved: '已缓解'
  };
  return labels[status];
}
function issueStatusClass(status: HealthIssueStatus) {
  const classes: Record<HealthIssueStatus, string> = {
    current: 'bg-danger-soft text-danger',
    pending_review: 'bg-warning-soft text-warning',
    resolved: 'bg-success-soft text-success'
  };
  return classes[status];
}
function abnormalGradeLabel(value: string) {
  const labels: Record<string, string> = {
    GENERAL_ABNORMAL: '一般异常',
    NOT_IMPORTANT: '不重要异常/轻度异常',
    IMPORTANT_A: '重要异常A类',
    IMPORTANT_B: '重要异常B类',
    UNCERTAIN: '不确定',
    CONFLICT: '冲突',
    NORMAL: '无异常'
  };
  return labels[value] ?? value;
}
function abnormalGradeClass(value: string) {
  const classes: Record<string, string> = {
    GENERAL_ABNORMAL: 'bg-warning-soft text-warning',
    NOT_IMPORTANT: 'bg-accent-soft text-accent',
    IMPORTANT_A: 'bg-danger-soft text-danger',
    IMPORTANT_B: 'bg-danger-soft text-danger',
    UNCERTAIN: 'bg-accent-soft text-accent',
    CONFLICT: 'bg-success-soft text-success',
    NORMAL: 'bg-success-soft text-success'
  };
  return classes[value] ?? 'bg-surface-secondary text-muted';
}
function editPatientSummary(detail: PatientDetail | undefined, plan: HealthPlanDetail) {
  const patient = detail?.patient;
  const parts = [patient?.name || plan.patientId, patient?.gender ? genderLabel(patient.gender) : '', patient?.birthDate ? `${dayjs().diff(dayjs(patient.birthDate), 'year')}岁` : ''].filter(Boolean);
  return parts.join(' ｜ ');
}
function primaryDiseaseText(plan: HealthPlanDetail) {
  const source = [...plan.diet, ...plan.exerciseRx.map(e => `${e.title}${e.description}`), ...plan.nutrition, ...plan.tcmRegulation].join('');
  if (source.includes('血压') || source.includes('高血压')) return '高血压';
  return '暂无疾病标签';
}
function recentSupplementText(plan: HealthPlanDetail) {
  if (!plan.ownerName) return '暂无人工补充';
  return `${formatDate(plan.createdAt)} ${plan.ownerName}补充`;
}

// 每日推荐摄入目标:医生人工设定值,患者端优先于公式估算;空值表示跟随公式(动态)
function DietCalorieTargetCard({
  plan,
  patientDetail,
  editable,
  value,
  onChange
}: {
  plan: HealthPlanDetail;
  patientDetail: PatientDetail | undefined;
  editable: boolean;
  value: string;
  onChange: (value: string) => void;
}) {
  const formulaText = plan.dietCalorieFormula !== null ? `公式参考值 ${plan.dietCalorieFormula} 千卡/天` : `公式参考值不可算（${plan.dietCalorieFormulaReason || '原因未知'}）`;
  const birthDate = patientDetail?.patient?.birthDate;
  const underAge = birthDate ? dayjs().diff(dayjs(birthDate), 'year') < 18 : false;
  return <section className="mt-4 rounded-xl border border-border bg-background p-5 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <div className="text-foreground text-sm font-medium">
            每日推荐摄入目标
          </div>
          <div className="text-muted mt-1 text-xs">{formulaText}</div>
        </div>
        {editable ? <div className="flex items-center gap-2">
            <SimpleInput aria-label="每日推荐摄入目标" className="w-40" value={value} placeholder="未设定，跟随公式" onChange={onChange} />
            <span className="text-muted text-sm">千卡/天</span>
          </div> : <div className="text-foreground text-sm">
            {plan.dietCalorieTarget !== null ? `医生设定 ${plan.dietCalorieTarget} 千卡/天` : '未设定，跟随公式估算'}
          </div>}
      </div>
      {editable && <div className="text-muted mt-3 text-xs">
          设定后患者端热量卡片优先采用该值；清空则回落公式估算（随档案与运动评估动态变化），发布方案后生效。
        </div>}
      {editable && underAge && <div className="text-warning mt-1 text-xs">
          患者未满 18 岁，公式估算不适用；仍可人工设定，请确认为临床决定。
        </div>}
    </section>;
}

// 按监测类型动态渲染时段选择,对不在合法集中的值显式标记"未知时段"
function MonitoringTimeSlotSelect({
  type,
  value,
  isDisabled,
  onChange
}: {
  type: string;
  value: string[];
  isDisabled?: boolean;
  onChange: (value: string[]) => void;
}) {
  const typeOptions = monitoringSlotsByType[type] ?? [];
  const options = getMonitoringTimeSlotOptions(type, value);
  const validValues = new Set(typeOptions.map(option => option.value));
  const unknownSlots = value.filter(v => !validValues.has(v));
  return <div>
      <SimpleMultiSelect value={value} options={options} isDisabled={isDisabled} onChange={onChange} />
      {unknownSlots.length > 0 && <div className="mt-1 text-xs text-danger">
          {unknownSlots.map(s => `未知时段: ${s}`).join('、')}
          ——请修正后再发布
        </div>}
    </div>;
}
// ---- 公共小组件 ----
// endregion
// niceDomain 把上下界对齐到 1/2/5 系列的整齐步长，避免出现 38.7 这种刻度
// endregion
// endregion
// SwipeRow 仅对可删除行启用左滑露出删除按钮,其他行直接透传内容
// endregion
// AI 巡查风险等级 → 徽章文案与色调
// 随访状态 → 徽章文案与色调, 已取消的不会出现在首页
// 复查提醒与随访卡片的统计范围说明
// 用户来源分类与条形配色, 顺序即展示顺序; 平台是「无院内记录」的兜底类, 用中性灰
// 各指标的口径说明, 集中放一处便于核对
// endregion
// 管理视角: 超管额外看到管理归属、覆盖、活跃趋势与功能使用; 健管师 / 运营员只看基础概览
// 超管的核心指标: 用真实的活跃与新增替代原先写死的两项 100%
// region 复查提醒与患者随访
// 管理视角和医生工作台共用, 统计范围由服务端按角色决定, 卡片描述里写明
// 上面一排底色统计格, 下面是近 7 天的明细列表
// endregion
// 用户来源分布: 横向条形, 全为 0(含数据未就绪)时按无数据占位
// 指标行:仿模板 KPI 行;纯数字走 KPI.Value,带 % 等符号的直接文本渲染
// endregion
if (import.meta.hot) {
  import.meta.hot.accept(newModule => {
    if (newModule && newModule.Route && typeof newModule.Route.clone === 'function') {
      newModule.Route.clone(Route);
    }
  });
}